/* ═══════════════════════════════════════════════════════════════
   Project Hub — Dashboard, DataTable list, dialog with
   click-to-edit fields and deliverables sub-dialog.
   ═══════════════════════════════════════════════════════════════ */

import React, { useState, useEffect, useMemo } from 'react';
import { Download, Check, Settings, Trash2, X, AlertTriangle, ChevronLeft } from 'lucide-react';
import { isWeb } from '../lib/platform';
import { DataTable, Column, GroupOption } from '../components/DataTable';
import { ProjectFormaTokens, resolveProjectTokenContent } from '../components/ProjectFormaTokens';
import { resolvePlaceholders, unboundPlaceholders } from '../../shared/forma-placeholders';
import { SearchableSelect } from '../components/SearchableSelect';
import EmptyState from '../components/EmptyState';
import { Button } from '../components/ui/button';
import { WidgetGrid, WidgetDef, WidgetLayoutEntry, WidgetSize } from '../components/WidgetGrid';
import { hasPermission } from '../../shared/permissions';
import type { AuthUser } from '../services/authService';
import { ProjectService } from '../services/projectService';
import { OrgService } from '../services/orgService';
import { AdminService } from '../services/adminService';
import { TimeService } from '../services/timeService';
import { TeamService } from '../services/teamService';
import { DepartmentService } from '../services/departmentService';
import { ProjectSpecService } from '../services/projectSpecService';
import { FormaService } from '../services/formaService';
import { prepareSpecsForExport, exportSpecsViaServer } from '../services/specExportPipeline';
import { realIssuances } from '../../shared/types';
import type {
  Project,
  Organization,
  ProjectType,
  BillingType,
  ProjectPhase,
  Deliverable,
  ProjectScope,
  ProjectScopeAssignment,
  TeamMember,
  ProjectAssignment,
  Team,
  TeamAssignment,
  HourForecast,
  ProjectSpec,
  StandardConfig,
} from '../../shared/types';
import { listCached, list as cacheList } from '../services/cloudCache';
// PM-platform widget bodies — financial / utilization / forecast metrics,
// plus the split Project Phase + Deliverables widgets (2026-07-20 divorce:
// a deliverable is name + due date + project, never phase-grouped).
import {
  BudgetVsActualHoursWidget,
  BudgetVsActualDollarsWidget,
  ForecastOutlookWidget,
  ARagingWidget,
  BurnRateWidget,
  TeamUtilizationWidget,
  PhasesWidget,
  DeliverablesWidget,
  ProfitabilityWidget,
  VarianceWidget,
  RecentActivityWidget,
  PhaseHealthWidget,
  RisksOpenItemsWidget,
  DashboardViewsBar,
  type ProjectWidgetCtx,
  type DashboardDrill,
} from './projectDashboardWidgets';
import { DashboardViewService } from '../services/dashboardViewService';
import type { DashboardView } from '../../shared/entities';
import type { BillingRole } from '../../shared/types';
import type { Department } from '../../shared/entities';

/**
 * Display-only sanitizer for project names imported from Word/Drive.
 *
 * Background: a chunk of imported project names carry a stray U+0096 (and a
 * lone U+0092) — these are Windows-1252 "smart" punctuation (0x96 = en dash,
 * 0x92 = right single quote) that got read as raw bytes instead of decoded,
 * so they landed in the DB as bare C1 control codepoints with no glyph. They
 * render as "tofu" boxes (□) mid-name, e.g. "Arlington ISD □ Atherton ES".
 * (Verified against prod: 111×U+0096, 1×U+0092 across 93/308 names.)
 *
 * This fixes the DISPLAY only — it never mutates stored data. We strip a small
 * set of glyph-less / replacement-prone codepoints:
 *   - U+FFFD replacement char
 *   - C0 controls (U+0000–U+001F, excl. tab/newline) and DEL (U+007F)
 *   - C1 controls (U+0080–U+009F)  ← the actual culprit here (U+0096/U+0092)
 *   - private-use area (U+E000–U+F8FF), a common source of unsupported tofu
 * An occurrence between two word characters becomes " — " (em dash + spaces)
 * so the original separator intent is preserved; an occurrence at an edge (or
 * adjacent to existing spaces) collapses to nothing. Result is whitespace-
 * tidied. Pure-ASCII names (the common case) pass straight through untouched.
 */
// Matches one-or-more consecutive "tofu"-prone codepoints (see doc above).
const TOFU_RE = /[\uFFFD\u0000-\u0008\u000B-\u000C\u000E-\u001F\u007F\u0080-\u009F\uE000-\uF8FF]+/g;
export function sanitizeProjectName(raw?: string | null): string {
  if (!raw) return raw ?? '';
  // Fast path: nothing offending → return as-is (no allocation churn).
  if (!TOFU_RE.test(raw)) return raw;
  TOFU_RE.lastIndex = 0; // reset: .test() advances lastIndex on a global regex
  // Restore Windows-1252 smart punctuation that was stored as bare C1 bytes
  // (0x80–0x9F) — e.g. 0x96 → en dash, 0x92 → apostrophe, 0x93/94 → curly quotes,
  // 0x85 → ellipsis — so "Parkhill JH □ Additions" → "Parkhill JH – Additions"
  // and "Owner□s" → "Owner's", instead of bluntly dashing them out.
  const C1_MAP: Record<number, string> = {
    0x82: '‚',
    0x83: 'ƒ',
    0x84: '„',
    0x85: '…',
    0x86: '†',
    0x87: '‡',
    0x88: 'ˆ',
    0x89: '‰',
    0x8a: 'Š',
    0x8b: '‹',
    0x8c: 'Œ',
    0x8e: 'Ž',
    0x91: '‘',
    0x92: '’',
    0x93: '“',
    0x94: '”',
    0x95: '•',
    0x96: '–',
    0x97: '—',
    0x98: '˜',
    0x99: '™',
    0x9a: 'š',
    0x9b: '›',
    0x9c: 'œ',
    0x9e: 'ž',
    0x9f: 'Ÿ',
  };
  const restored = raw.replace(/[-]/g, (ch) => C1_MAP[ch.charCodeAt(0)] ?? ch);
  const cleaned = restored.replace(TOFU_RE, (_m, offset: number, str: string) => {
    const prev = str[offset - 1];
    const next = str[offset + _m.length];
    // Inter-word (real chars on both sides, neither already a space) → em-dash.
    if (prev && next && !/\s/.test(prev) && !/\s/.test(next)) return ' — ';
    // Edge or space-adjacent → drop it; surrounding spaces are tidied below.
    return ' ';
  });
  // Collapse any doubled spaces we introduced and trim the ends.
  return cleaned.replace(/\s{2,}/g, ' ').trim();
}

/**
 * Migrate saved dashboard layouts (user_prefs blob + saved Views) from the
 * retired combined "Schedule & Milestones" widget to its two successors:
 * schedule_timeline → phases + deliverables (deliverables keeps the saved
 * size so an expanded schedule stays an expanded Gantt). Idempotent; runs
 * at render so stored configs migrate lazily on next save.
 */
function migrateDashboardLayout(entries: WidgetLayoutEntry[]): WidgetLayoutEntry[] {
  if (!entries.some((e) => e.id === 'schedule_timeline')) return entries;
  const out: WidgetLayoutEntry[] = [];
  const has = (id: string) => entries.some((e) => e.id === id);
  for (const e of entries) {
    if (e.id !== 'schedule_timeline') {
      out.push(e);
      continue;
    }
    if (!has('phases')) out.push({ id: 'phases', size: 'standard', hidden: e.hidden });
    if (!has('deliverables'))
      out.push({
        id: 'deliverables',
        size: e.size === 'expanded' ? 'expanded' : 'standard',
        hidden: e.hidden,
      });
  }
  return out;
}

interface ProjectsProps {
  toast: (msg: string, isError?: boolean) => void;
  onOpenProjectSpec?: (specId: string, projectId: string) => void;
  initialProjectId?: string;
  authUser?: AuthUser | null;
  /** Cross-page navigation function. Dashboard widgets call this via the
   *  drill router to land on TimeExpense / Invoices / Team etc. */
  navigate?: (link: import('../types/navigation').DeepLink) => void;
  /** One-shot deep link landing on this page (e.g. open project X). */
  deepLink?: {
    view: 'projects';
    projectId?: string;
    tab?: 'dashboard' | 'specs' | 'formas';
  } | null;
  /** Open a forma in the designer (host swaps to the editor view). */
  onOpenForma?: (formaId: string) => void;
  onDeepLinkConsumed?: () => void;
}

function Projects({
  toast,
  authUser,
  onOpenProjectSpec,
  onOpenForma,
  initialProjectId,
  navigate,
  deepLink,
  onDeepLinkConsumed,
}: ProjectsProps) {
  // Consolidated permission flags — read once so we can reuse them in JSX.
  // The server also enforces these; the UI gates just hide buttons the user
  // can't use so they don't hit a "Permission denied" on click.
  const perms = authUser?.permissions;
  const canDeleteSpecs = hasPermission(perms, 'specs.delete');
  const canCreateProject = hasPermission(perms, 'projects.create');
  const canEditProject = hasPermission(perms, 'projects.edit');
  const canArchiveProject = hasPermission(perms, 'projects.archive');
  const canDeleteProject = hasPermission(perms, 'projects.delete');
  const canAssignTeam =
    hasPermission(perms, 'projects.assign_team') || hasPermission(perms, 'projects.assign_anyone');
  const [selectedSpecIds, setSelectedSpecIds] = useState<Set<string>>(new Set());
  const [projects, setProjects] = useState<Project[]>(() => listCached<Project>('projects'));
  const [orgs, setOrgs] = useState<Organization[]>(() => listCached<Organization>('organizations'));
  const [projectTypes, setProjectTypes] = useState<ProjectType[]>(() =>
    listCached<ProjectType>('project_types')
  );
  const [billingTypes, setBillingTypes] = useState<BillingType[]>(() =>
    listCached<BillingType>('billing_types')
  );
  const [firmPhases, setFirmPhases] = useState<{ id: string; name: string; sortOrder: number }[]>(
    []
  );
  const [allPhases, setAllPhases] = useState<ProjectPhase[]>([]);
  const [allDeliverables, setAllDeliverables] = useState<Deliverable[]>([]);
  const [allScopes, setAllScopes] = useState<ProjectScope[]>([]);
  const [allScopeAssignments, setAllScopeAssignments] = useState<ProjectScopeAssignment[]>([]);
  const [dialogScopeAssignments, setDialogScopeAssignments] = useState<ProjectScopeAssignment[]>(
    []
  );

  // Detail page state (replaces dialog)
  const [detailView, setDetailView] = useState(false);
  const [editing, setEditing] = useState<Project | null>(null);

  // Dashboard widget state — customize mode + current layout.
  // Layout lives on the signed-in user's team_members row so it follows them
  // between devices. When no custom layout exists, DEFAULT_PROJECT_DASHBOARD
  // below is used (picked once, then the user can tweak freely).
  const [customizingDashboard, setCustomizingDashboard] = useState(false);
  const [projectDetailTab, setProjectDetailTab] = useState<'dashboard' | 'specs' | 'formas'>(
    'dashboard'
  );
  const [dashboardLayout, setDashboardLayoutState] = useState<WidgetLayoutEntry[]>([]);

  // Saved Views — same pattern as Forecasting. surface = 'project_dashboard'.
  // A view stores { layout: WidgetLayoutEntry[] } and can be marked default
  // or shared with the firm.
  const [dashboardViews, setDashboardViews] = useState<DashboardView[]>([]);
  const [activeViewId, setActiveViewId] = useState<string | null>(null);
  const [showSaveViewDialog, setShowSaveViewDialog] = useState(false);
  const [saveViewName, setSaveViewName] = useState('');
  const [saveViewShared, setSaveViewShared] = useState(false);
  const dashboardSurface = 'project_dashboard';

  const reloadDashboardViews = async () => {
    const userId = authUser?.teamMemberId || authUser?.id;
    if (!userId) return;
    try {
      const all = await DashboardViewService.listForSurface(dashboardSurface, userId);
      setDashboardViews(all);
    } catch (err) {
      console.error('[dashboard views load]', err);
    }
  };

  // Load the user's saved layout once per session. Priority:
  //   1. If there is a default DashboardView, use its layout.
  //   2. Else fall back to the legacy team_members.dashboardLayouts.project_detail blob.
  //   3. Else empty (DEFAULT_PROJECT_DASHBOARD wins inside WidgetGrid).
  useEffect(() => {
    const memberId = authUser?.teamMemberId || authUser?.id;
    if (!memberId) return;
    (async () => {
      try {
        await reloadDashboardViews();
        const def = await DashboardViewService.getDefault(dashboardSurface, memberId);
        if (def && Array.isArray((def.config as any)?.layout)) {
          setDashboardLayoutState((def.config as any).layout);
          setActiveViewId(def.id);
          return;
        }
        const allMembers = await TeamService.list();
        const me = allMembers.find((m: any) => m.id === memberId);
        const saved = (me as any)?.dashboardLayouts?.project_detail;
        if (Array.isArray(saved) && saved.length > 0) setDashboardLayoutState(saved);
      } catch {
        /* ignore — user gets default layout */
      }
    })();
  }, [authUser?.teamMemberId, authUser?.id]);

  const saveDashboardLayout = async (next: WidgetLayoutEntry[]) => {
    setDashboardLayoutState(next);
    const memberId = authUser?.teamMemberId || authUser?.id;
    if (!memberId) return;
    // If a saved view is active, write the new layout straight back into it
    // so changes follow the view; otherwise persist to the legacy blob.
    try {
      if (activeViewId) {
        const v = dashboardViews.find((x) => x.id === activeViewId);
        if (v && v.userId === memberId) {
          await DashboardViewService.save({
            ...v,
            config: { ...(v.config || {}), layout: next },
          });
          await reloadDashboardViews();
          return;
        }
      }
      const allMembers = await TeamService.list();
      const me = allMembers.find((m: any) => m.id === memberId);
      if (!me) return;
      const layouts = { ...(me as any).dashboardLayouts, project_detail: next };
      await TeamService.save({ ...me, dashboardLayouts: layouts } as any);
    } catch (err) {
      console.error('[dashboard layout save]', err);
    }
  };

  const switchToView = (viewId: string | null) => {
    if (viewId === null) {
      setActiveViewId(null);
      // Reset to legacy blob layout (or default)
      const memberId = authUser?.teamMemberId || authUser?.id;
      if (!memberId) {
        setDashboardLayoutState([]);
        return;
      }
      (async () => {
        try {
          const allMembers = await TeamService.list();
          const me = allMembers.find((m: any) => m.id === memberId);
          const saved = (me as any)?.dashboardLayouts?.project_detail;
          setDashboardLayoutState(Array.isArray(saved) ? saved : []);
        } catch {
          setDashboardLayoutState([]);
        }
      })();
      return;
    }
    const v = dashboardViews.find((x) => x.id === viewId);
    if (!v) return;
    const layout = (v.config as any)?.layout;
    if (Array.isArray(layout)) {
      setDashboardLayoutState(layout);
      setActiveViewId(viewId);
    }
  };

  const saveAsNewView = async () => {
    const userId = authUser?.teamMemberId || authUser?.id;
    if (!userId || !saveViewName.trim()) return;
    const view: DashboardView = {
      id: '',
      userId,
      surface: dashboardSurface,
      name: saveViewName.trim(),
      config: { layout: dashboardLayout },
      shared: saveViewShared,
      isDefault: false,
    };
    const saved = await DashboardViewService.save(view);
    setActiveViewId(saved.id);
    setShowSaveViewDialog(false);
    setSaveViewName('');
    setSaveViewShared(false);
    await reloadDashboardViews();
    toast(`View "${saved.name}" saved.`);
  };

  const deleteActiveView = async () => {
    if (!activeViewId) return;
    const v = dashboardViews.find((x) => x.id === activeViewId);
    if (!v) return;
    if (!confirm(`Delete saved view "${v.name}"?`)) return;
    await DashboardViewService.delete(activeViewId);
    setActiveViewId(null);
    await reloadDashboardViews();
    toast(`View "${v.name}" deleted.`);
  };

  const setActiveAsDefault = async () => {
    const userId = authUser?.teamMemberId || authUser?.id;
    if (!userId || !activeViewId) return;
    await DashboardViewService.setDefault(activeViewId, dashboardSurface, userId);
    await reloadDashboardViews();
    toast('Default view updated.');
  };

  // Snapshot / Export — open browser print dialog scoped to the grid.
  // The user's existing system print → "Save as PDF" handles the file output;
  // we just inject a print stylesheet that hides everything outside the grid.
  const exportDashboardPdf = () => {
    const grid = document.getElementById('project-dashboard-grid');
    if (!grid) return;
    window.print();
  };

  const [dirty, setDirty] = useState(false);
  const [phases, setPhases] = useState<ProjectPhase[]>([]);
  const [deliverables, setDeliverables] = useState<Deliverable[]>([]);
  const [editMode, setEditMode] = useState(false); // false = view mode for existing projects

  // Team state
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [teamAssignments, setTeamAssignments] = useState<TeamAssignment[]>([]);
  const [projectAssignments, setProjectAssignments] = useState<ProjectAssignment[]>([]);
  // Departments — surfaced as colored pills on the project Team panel so
  // a member's discipline (MEPT, BIM, …) is visible alongside team affinity.
  const [departments, setDepartments] = useState<Department[]>([]);
  // Forecasts for the next 4 weeks — drives the "Xh / no forecast" pill
  // on each project Team panel row so a PM immediately sees who's been
  // assigned but doesn't have planned work coming up.
  const [upcomingForecasts, setUpcomingForecasts] = useState<HourForecast[]>([]);
  // Billing roles for the BudgetVsActualDollars widget — needs rate cards
  // to compute actual cost when team_member.hourlyRate is null.
  const [billingRoles, setBillingRoles] = useState<BillingRole[]>([]);
  useEffect(() => {
    AdminService.billingRoles
      .list()
      .then(setBillingRoles)
      .catch(() => setBillingRoles([]));
  }, []);
  const [showMemberPicker, setShowMemberPicker] = useState(false);
  const [showTeamPicker, setShowTeamPicker] = useState(false);

  // Project specs state
  const [projectSpecs, setProjectSpecs] = useState<ProjectSpec[]>([]);
  // Firm-wide project specs — drives the "Specs" count column on the hub table.
  // Loaded in loadData() so the count refreshes on every specforma-sync-complete.
  const [allProjectSpecs, setAllProjectSpecs] = useState<ProjectSpec[]>(() =>
    listCached<ProjectSpec>('project_specs')
  );

  // Export-specs modal state
  const [showExportModal, setShowExportModal] = useState(false);
  // Which specs the export modal will export — defaults to all on open.
  const [exportSelIds, setExportSelIds] = useState<Set<string>>(new Set());
  const [importingDocx, setImportingDocx] = useState(false);
  const [exportFormat, setExportFormat] = useState<'docx' | 'pdf' | 'both'>('docx');
  const [exportFormaId, setExportFormaId] = useState<string>('');
  const [exportDir, setExportDir] = useState<string>('');
  const [exporting, setExporting] = useState(false);
  const [formas, setFormas] = useState<StandardConfig[]>([]);
  // Deliverable stamp — fills the {Deliverable} forma token on every page
  const [exportDeliverable, setExportDeliverable] = useState<string>('');
  const [exportRevisionMarks, setExportRevisionMarks] = useState(false);
  const [exportDeliverables, setExportDeliverables] = useState<Array<{ id: string; name: string }>>(
    []
  );
  useEffect(() => {
    if (!showExportModal || !editing?.id) return;
    // Pre-select every spec (or the table's pre-selection, if any).
    setExportSelIds(
      selectedSpecIds.size > 0 ? new Set(selectedSpecIds) : new Set(projectSpecs.map((s) => s.id))
    );
    TimeService.listDeliverables(editing.id)
      .then((ds) => setExportDeliverables(ds.map((d) => ({ id: d.id, name: d.name }))))
      .catch(() => setExportDeliverables([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showExportModal]);
  useEffect(() => {
    FormaService.list()
      .then(setFormas)
      .catch(() => setFormas([]));
  }, []);

  // Bulk delete state
  const [deleteMode, setDeleteMode] = useState(false);
  const [deleteSelected, setDeleteSelected] = useState<Set<string>>(new Set());

  // ─── Project Drive mapping ───
  // Root path stored in localStorage (per-laptop). Later: firm-scoped in DB.
  const [projectDriveRoot, setProjectDriveRoot] = useState<string>(() => {
    try {
      return localStorage.getItem('specforma.projectDriveRoot') || '';
    } catch {
      return '';
    }
  });
  const [scanningDrive, setScanningDrive] = useState(false);

  // Folders found on the drive that have no matching project in the DB.
  // After Auto-match finds these, the import modal lets the user create them
  // as new projects (project number + folder path + edited name).
  type UnmatchedFolder = {
    folderPath: string;
    folderName: string;
    projectNumber: string;
    namePart: string;
    include: boolean;
    editedName: string;
  };
  const [unmatchedFolders, setUnmatchedFolders] = useState<UnmatchedFolder[]>([]);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importingFolders, setImportingFolders] = useState(false);

  function saveProjectDriveRoot(p: string) {
    setProjectDriveRoot(p);
    try {
      localStorage.setItem('specforma.projectDriveRoot', p);
    } catch {}
  }
  async function pickProjectDriveRoot() {
    const dir = await (window as any).api?.selectFolderDialog?.();
    if (dir) saveProjectDriveRoot(dir);
  }
  // Normalize project numbers so "23012-01", "23012_1", "23012 1" all collide.
  // Strip every non-digit, then re-segment as {first5-7}-{last}. Anything that
  // doesn't have a trailing short suffix stays as pure digits.
  function normalizeProjectNumber(raw: string): string {
    const digitsOnly = (raw || '').replace(/\D/g, '');
    return digitsOnly;
  }

  // Auto-match: scan root, map folder.projectNumber -> project, persist project.folderPath.
  // Any folder whose number doesn't exist in the DB is collected as an import
  // candidate and the user is prompted to add them as new projects.
  async function autoMatchProjectDrive() {
    if (!projectDriveRoot) {
      toast('Pick a Project Drive root first', true);
      return;
    }
    if (!(window as any).api?.scanProjectDrive) {
      toast('Scan API not available — restart the app.', true);
      return;
    }
    setScanningDrive(true);
    try {
      const folders = (await (window as any).api.scanProjectDrive(projectDriveRoot)) as Array<{
        folderName: string;
        folderPath: string;
        projectNumber: string;
        namePart: string;
      }>;

      // Build project-number → folderPath map from the drive.
      const folderByNum = new Map<string, (typeof folders)[number]>();
      for (const f of folders) folderByNum.set(normalizeProjectNumber(f.projectNumber), f);

      // Build project-number set from the DB side.
      const dbNums = new Set<string>();
      for (const p of projects) {
        const n = normalizeProjectNumber(p.projectNumber || '');
        if (n) dbNums.add(n);
      }

      // 1) Link every DB project whose number appears on the drive.
      let matched = 0;
      let updated = 0;
      for (const p of projects) {
        const num = normalizeProjectNumber(p.projectNumber || '');
        if (!num) continue;
        const hit = folderByNum.get(num);
        if (!hit) continue;
        matched++;
        if (p.folderPath !== hit.folderPath) {
          await ProjectService.save({
            ...p,
            folderPath: hit.folderPath,
            updatedAt: new Date().toISOString(),
          });
          updated++;
        }
      }

      // 2) Collect folders the app doesn't know about — prompt to import.
      // Only full NNNNN-NN numbers may become projects. The scanner already
      // enforces this, but guard here too — a loose scanner once mass-created
      // 398 junk projects from bare 5-digit folder prefixes (2026-06-09).
      const unknown: UnmatchedFolder[] = [];
      for (const f of folders) {
        if (!/^\d{5}-\d{2}$/.test(f.projectNumber)) continue;
        const n = normalizeProjectNumber(f.projectNumber);
        if (!n || dbNums.has(n)) continue;
        unknown.push({
          folderPath: f.folderPath,
          folderName: f.folderName,
          projectNumber: f.projectNumber,
          namePart: f.namePart,
          include: true,
          editedName: f.namePart || f.folderName,
        });
      }

      if (matched === 0 && unknown.length === 0) {
        toast(
          `Scanned ${folders.length} folder${folders.length === 1 ? '' : 's'} but no project numbers matched. Check folder naming or pick a different root.`,
          true
        );
      } else {
        const linkedMsg =
          matched > 0
            ? `Auto-match: ${matched}/${projects.length} linked (${updated} updated, ${folders.length} folders scanned)`
            : `Scanned ${folders.length} folder${folders.length === 1 ? '' : 's'}`;
        toast(linkedMsg);
      }

      if (unknown.length > 0 && canCreateProject) {
        setUnmatchedFolders(unknown);
        setShowImportModal(true);
      }
    } catch (err: any) {
      toast(`Scan failed: ${err.message || err}`, true);
    } finally {
      setScanningDrive(false);
    }
  }

  // Import selected unmatched folders as brand-new projects.
  async function importUnmatchedFolders() {
    const toImport = unmatchedFolders.filter((u) => u.include);
    if (toImport.length === 0) {
      setShowImportModal(false);
      return;
    }
    setImportingFolders(true);
    let created = 0;
    try {
      for (const u of toImport) {
        const newProj: Partial<Project> = {
          id: '',
          name: u.editedName.trim() || u.folderName,
          projectNumber: u.projectNumber,
          folderPath: u.folderPath,
          status: 'active',
          selections: {},
          createdAt: '',
          updatedAt: '',
        };
        try {
          await ProjectService.save(newProj as Project);
          created++;
        } catch (err) {
          console.error('[import folder]', u.projectNumber, err);
        }
      }
      toast(`Imported ${created} project${created === 1 ? '' : 's'} from the drive`);
      await loadData();
      setUnmatchedFolders([]);
      setShowImportModal(false);
    } finally {
      setImportingFolders(false);
    }
  }

  useEffect(() => {
    loadData();
  }, []);
  useEffect(() => {
    const handler = (ev: Event) => {
      loadData();
      if (detailView && editing?.id) {
        loadProjectDetails(editing.id);
        AdminService.projectScopeAssignments
          .list(editing.id)
          .then(setDialogScopeAssignments)
          .catch(() => {});
        const tables: string[] = (ev as CustomEvent)?.detail?.tables || [];
        if (tables.includes('default_phases')) syncAndLoadPhases(editing.id);
      }
    };
    window.addEventListener('specforma-sync-complete', handler);
    return () => window.removeEventListener('specforma-sync-complete', handler);
  }, [detailView, editing?.id]);

  async function loadData(retry = true) {
    try {
      // Forecast window for the "next 4 weeks" indicator on the Team
      // panel — start at next Monday, 28 days ahead. Skips the current
      // in-progress week so the signal is purely forward-looking.
      const todayIso = (() => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      })();
      const monIso = (iso: string) => {
        const [y, m, d] = iso.split('-').map(Number);
        const dt = new Date(Date.UTC(y, m - 1, d));
        const dow = dt.getUTCDay();
        const back = (dow + 6) % 7;
        dt.setUTCDate(dt.getUTCDate() - back);
        const pad = (n: number) => String(n).padStart(2, '0');
        return `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}`;
      };
      const addIso = (iso: string, days: number) => {
        const [y, m, d] = iso.split('-').map(Number);
        const dt = new Date(Date.UTC(y, m - 1, d));
        dt.setUTCDate(dt.getUTCDate() + days);
        const pad = (n: number) => String(n).padStart(2, '0');
        return `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}`;
      };
      const fcStart = addIso(monIso(todayIso), 7);
      const fcEnd = addIso(fcStart, 28);
      // Promise.allSettled — one failing source degrades to an empty result
      // instead of blanking the whole page load (design law: data never
      // blank, always live). Each settled result is used only if fulfilled.
      const results = await Promise.allSettled([
        ProjectService.list(),
        OrgService.list(),
        AdminService.projectTypes.list(),
        AdminService.billingTypes.list(),
        AdminService.defaultPhases.list(),
        AdminService.projectScopes.list(),
        AdminService.projectScopeAssignments.list(),
        cacheList<ProjectPhase>('project_phases'),
        cacheList<Deliverable>('deliverables'),
        TeamService.list(),
        TeamService.listTeams(),
        TeamService.listTeamAssignments(),
        DepartmentService.list(),
        TimeService.listForecastsForRange(fcStart, fcEnd),
        ProjectSpecService.list(),
      ] as const);
      const settled = <T,>(r: PromiseSettledResult<T>, fallback: T): T => {
        if (r.status === 'fulfilled') return r.value;
        console.error('[projects] loadData source failed:', r.reason);
        return fallback;
      };
      const p = settled(results[0], [] as Project[]);
      const o = settled(results[1], [] as Organization[]);
      const pt = settled(results[2], [] as ProjectType[]);
      const bt = settled(results[3], [] as BillingType[]);
      const fp = settled(results[4], [] as { id: string; name: string; sortOrder: number }[]);
      const sc = settled(results[5], [] as ProjectScope[]);
      const sca = settled(results[6], [] as ProjectScopeAssignment[]);
      const allPh = settled(results[7], [] as ProjectPhase[]);
      const allDel = settled(results[8], [] as Deliverable[]);
      const mbrs = settled(results[9], [] as TeamMember[]);
      const tms = settled(results[10], [] as Team[]);
      const tAs = settled(results[11], [] as TeamAssignment[]);
      const depts = settled(results[12], [] as Department[]);
      const fcs = settled(results[13], [] as HourForecast[]);
      const pSpecs = settled(results[14], [] as ProjectSpec[]);
      console.log(
        '[projects] loadData got',
        p.length,
        'projects,',
        allPh.length,
        'phases,',
        allDel.length,
        'deliverables'
      );
      setProjects(p);
      setOrgs(o);
      setProjectTypes(pt);
      setBillingTypes(bt);
      setFirmPhases(fp);
      setAllScopes(sc);
      setAllScopeAssignments(sca);
      setAllPhases(allPh);
      setAllDeliverables(allDel);
      setMembers(mbrs);
      setTeams(tms);
      setTeamAssignments(tAs);
      setDepartments(depts);
      setUpcomingForecasts(fcs);
      setAllProjectSpecs(pSpecs);
    } catch (e) {
      console.error('Projects loadData failed:', e);
      if (retry) setTimeout(() => loadData(false), 1500);
    }
  }

  async function syncAndLoadPhases(projectId: string) {
    try {
      // ONE source of truth: TimeService.syncPhasesWithDefaults mirrors the
      // admin-center Phases list (order included) into this project — the
      // same call every dashboard widget uses, so they can never disagree.
      setPhases(await TimeService.syncPhasesWithDefaults(projectId));
    } catch (err: any) {
      console.error('syncAndLoadPhases error:', err);
      toast('Failed to load phases: ' + (err.message || 'Unknown error'), true);
      setPhases([]);
    }
  }

  async function loadProjectDetails(projectId: string) {
    try {
      const d = await TimeService.listDeliverables(projectId);
      setDeliverables(d);
    } catch (err: any) {
      console.error('loadDeliverables error:', err);
      toast('Failed to load deliverables: ' + (err.message || 'Unknown error'), true);
      setDeliverables([]);
    }
    try {
      const pa = await TeamService.listAssignments(undefined, projectId);
      setProjectAssignments(pa);
    } catch (err: any) {
      console.error('loadProjectAssignments error:', err);
      setProjectAssignments([]);
    }
    await syncAndLoadPhases(projectId);
    try {
      const specs = await ProjectSpecService.listByProject(projectId);
      setProjectSpecs(specs);
    } catch (err: any) {
      console.error('loadProjectSpecs error:', err);
      setProjectSpecs([]);
    }
  }

  // ─── Helpers ───
  function orgName(id?: string) {
    return orgs.find((o) => o.id === id)?.name || '';
  }
  function typeName(id?: string) {
    return projectTypes.find((t) => t.id === id)?.name || '';
  }
  function billingName(id?: string) {
    return billingTypes.find((t) => t.id === id)?.name || '';
  }
  function getClient(p: Project) {
    return orgName(p.clientOrgId) || p.clientName || '';
  }
  function getProjectScopes(p: Project): ProjectScope[] {
    // Dedupe: a project is either in a scope or not — one badge per scope regardless of how many
    // duplicate assignment rows exist in the DB.
    const scopeIds = new Set(
      allScopeAssignments.filter((a) => a.projectId === p.id).map((a) => a.scopeId)
    );
    return allScopes.filter((s) => scopeIds.has(s.id));
  }

  async function setProjectPhase(p: Project, phaseName: string) {
    const projPhases = allPhases
      .filter((ph) => ph.projectId === p.id)
      .sort((a, b) => a.sortOrder - b.sortOrder);
    const targetIdx = projPhases.findIndex((ph) => ph.name === phaseName);
    if (targetIdx === -1) return;

    const clicked = projPhases[targetIdx];

    if (clicked.status === 'in-progress') {
      // Second click — complete this phase (all before stay complete, all after stay pending)
      for (let i = 0; i < projPhases.length; i++) {
        const newStatus = i <= targetIdx ? 'complete' : 'pending';
        if (projPhases[i].status !== newStatus) {
          await TimeService.savePhase({ ...projPhases[i], status: newStatus as any });
        }
      }
    } else {
      // First click — set this to in-progress, all before complete, all after pending
      for (let i = 0; i < projPhases.length; i++) {
        const newStatus = i < targetIdx ? 'complete' : i === targetIdx ? 'in-progress' : 'pending';
        if (projPhases[i].status !== newStatus) {
          await TimeService.savePhase({ ...projPhases[i], status: newStatus as any });
        }
      }
    }

    // If all phases are now complete, set project status to 'closed' (complete)
    const allComplete =
      projPhases.every((ph, i) => (i <= targetIdx ? true : ph.status === 'complete')) &&
      clicked.status === 'in-progress';
    if (allComplete && targetIdx === projPhases.length - 1) {
      // Last phase was in-progress and we just completed it — all done
    }
    // Re-check after saves
    await loadData();
    // Check if all phases ended up complete
    const updatedPhases = (await TimeService.listPhases(p.id)).sort(
      (a, b) => a.sortOrder - b.sortOrder
    );

    // Mirror the (single) in-progress phase to project.currentPhaseId so the
    // server-side hour-stamp trigger (migration 017) has a real phase ID to
    // attach to every forecast / hour entry. NULL when nothing is in-progress.
    const activePhase = updatedPhases.find((ph) => ph.status === 'in-progress');
    const desiredCurrentPhaseId = activePhase ? activePhase.id : null;
    if ((p.currentPhaseId || null) !== desiredCurrentPhaseId) {
      const updatedProj = await ProjectService.save({
        ...p,
        currentPhaseId: desiredCurrentPhaseId,
      });
      if (editing?.id === p.id) {
        setEditing((prev) =>
          prev ? { ...prev, currentPhaseId: updatedProj.currentPhaseId } : prev
        );
      }
    }

    if (updatedPhases.length > 0 && updatedPhases.every((ph) => ph.status === 'complete')) {
      if (p.status !== 'closed') {
        await ProjectService.save({ ...p, status: 'closed' });
        await loadData();
        if (editing?.id === p.id)
          setEditing((prev) => (prev ? { ...prev, status: 'closed' } : prev));
        toast('All phases complete — project marked as Complete');
      }
    }
  }

  function getProjectPhase(p: Project): string {
    const projPhases = allPhases
      .filter((ph) => ph.projectId === p.id)
      .sort((a, b) => a.sortOrder - b.sortOrder);
    if (projPhases.length === 0) return '';
    const inProgress = projPhases.filter((ph) => ph.status === 'in-progress');
    if (inProgress.length > 0) return inProgress[inProgress.length - 1].name;
    const complete = projPhases.filter((ph) => ph.status === 'complete');
    if (complete.length === projPhases.length)
      return projPhases[projPhases.length - 1].name + ' (Done)';
    const pending = projPhases.filter((ph) => ph.status === 'pending');
    if (pending.length > 0) return pending[0].name;
    return projPhases[0].name;
  }

  function getProjectDeliverables(p: Project, opts?: { includeIssued?: boolean }): Deliverable[] {
    return allDeliverables
      .filter((d) => d.projectId === p.id)
      .filter((d) => (opts?.includeIssued ? true : !d.issued))
      .sort((a, b) => {
        if (a.dueDate && b.dueDate) return String(a.dueDate).localeCompare(String(b.dueDate));
        if (a.dueDate) return -1;
        if (b.dueDate) return 1;
        return 0;
      });
  }

  // ─── Dashboard stats ───
  const activeProjects = projects.filter((p) => p.status === 'active');
  const closedProjects = projects.filter((p) => p.status === 'closed');

  // Each project is in exactly ONE phase at a time (linear progression), so the
  // headline counts PROJECTS by where they sit — not raw phase-instances (which made
  // "pending" balloon to every unreached future phase across every project).
  const phaseStats = useMemo(() => {
    let pending = 0;
    let inProgress = 0;
    let complete = 0;
    for (const proj of projects) {
      const pp = allPhases.filter((ph) => ph.projectId === proj.id);
      if (pp.length === 0) continue;
      if (pp.some((ph) => ph.status === 'in-progress')) inProgress++;
      else if (pp.every((ph) => ph.status === 'complete')) complete++;
      else pending++; // has phases but none started yet
    }
    return { pending, inProgress, complete, total: pending + inProgress + complete };
  }, [allPhases, projects]);

  const deliverableStats = useMemo(() => {
    const pending = allDeliverables.filter((d) => d.status === 'pending').length;
    const inProgress = allDeliverables.filter((d) => d.status === 'in-progress').length;
    const complete = allDeliverables.filter((d) => d.status === 'complete').length;
    return { pending, inProgress, complete, total: allDeliverables.length };
  }, [allDeliverables]);

  // ─── Detail Page Navigation ───
  // Auto-open project detail when returning from a project spec
  useEffect(() => {
    if (initialProjectId && projects.length > 0 && !detailView) {
      const proj = projects.find((p) => p.id === initialProjectId);
      if (proj) openEdit(proj);
    }
  }, [initialProjectId, projects.length]);

  function openNew() {
    setEditing({
      id: '',
      name: '',
      projectNumber: '',
      status: 'active',
      selections: {},
      createdAt: '',
      updatedAt: '',
    });
    setDirty(true);
    setPhases([]);
    setDeliverables([]);
    setProjectAssignments([]);
    setProjectSpecs([]);
    setShowMemberPicker(false);
    setShowTeamPicker(false);
    setEditMode(true);
    setDetailView(true);
    setProjectDetailTab('dashboard');
  }
  function openEdit(proj: Project, tab: 'dashboard' | 'specs' | 'formas' = 'dashboard') {
    setEditing(JSON.parse(JSON.stringify(proj)));
    setDirty(false);
    setShowMemberPicker(false);
    setShowTeamPicker(false);
    setEditMode(false);
    setDetailView(true);
    setProjectDetailTab(tab);
    if (proj.id) {
      loadProjectDetails(proj.id);
      AdminService.projectScopeAssignments
        .list(proj.id)
        .then(setDialogScopeAssignments)
        .catch(() => setDialogScopeAssignments([]));
    }
  }
  function closeDetail() {
    if (dirty && !confirm('Discard unsaved changes?')) return;
    setDetailView(false);
    setEditing(null);
    setDirty(false);
    setEditMode(false);
  }
  function update(updates: Partial<Project>) {
    setEditing((prev) => (prev ? { ...prev, ...updates } : prev));
    setDirty(true);
  }

  async function toggleScope(scopeId: string) {
    if (!editing?.id) return;
    // Check against fresh data, not stale cached state — prevents rapid-click duplicates
    const current = await AdminService.projectScopeAssignments.list(editing.id);
    const existingForScope = current.filter((a) => a.scopeId === scopeId);
    if (existingForScope.length > 0) {
      // Toggle OFF: delete ALL assignments for this scope (cleans up any duplicates too)
      for (const a of existingForScope) {
        await AdminService.projectScopeAssignments.delete(a.id);
      }
    } else {
      // Toggle ON: create exactly one
      await AdminService.projectScopeAssignments.save({
        id: '',
        projectId: editing.id,
        scopeId,
        createdAt: '',
      });
    }
    const updated = await AdminService.projectScopeAssignments.list(editing.id);
    setDialogScopeAssignments(updated);
    await loadData();
  }

  async function saveProject() {
    if (!editing) return;
    if (!editing.name.trim()) {
      toast('Enter a project name', true);
      return;
    }
    try {
      const saved = await ProjectService.save(editing);
      if (!editing.id && saved.id) {
        try {
          for (let i = 0; i < firmPhases.length; i++) {
            await TimeService.savePhase({
              id: '',
              projectId: saved.id,
              name: firmPhases[i].name,
              description: '',
              status: 'pending',
              sortOrder: i,
              createdAt: '',
              updatedAt: '',
            });
          }
        } catch (err) {
          console.error('Phase creation error on new project:', err);
        }
      }
      // Mirror PIC/PM onto project_assignments so the team panel reflects them.
      // This single source of truth: the project record owns picId/pmId, and we
      // keep exactly one pic / one pm assignment row on project_assignments in sync.
      if (saved.id) {
        try {
          await syncPicPmAssignments(saved.id, saved.picId, saved.pmId);
        } catch (err) {
          console.error('PIC/PM assignment sync failed:', err);
        }
      }
      const saved2 = saved;
      await loadData();
      setDirty(false);
      // Stay on detail page if editing existing, go back if new
      if (!editing.id && saved2.id) {
        // New project — load it in detail view
        const updated = (await ProjectService.list()).find((p) => p.id === saved2.id);
        if (updated) {
          setEditing(JSON.parse(JSON.stringify(updated)));
          setEditMode(false);
          loadProjectDetails(saved2.id);
          AdminService.projectScopeAssignments
            .list(saved2.id)
            .then(setDialogScopeAssignments)
            .catch(() => setDialogScopeAssignments([]));
        }
      } else {
        // Existing project — refresh editing state
        const updated = (await ProjectService.list()).find((p) => p.id === editing.id);
        if (updated) {
          setEditing(JSON.parse(JSON.stringify(updated)));
          setEditMode(false);
        }
      }
      toast('Project saved');
    } catch (err: any) {
      toast('Save failed: ' + (err.message || 'Unknown error'), true);
    }
  }

  // Bulk delete
  function toggleDeleteSelect(id: string) {
    setDeleteSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function bulkDelete() {
    if (deleteSelected.size === 0) return;
    const names = projects
      .filter((p) => deleteSelected.has(p.id))
      .map((p) => `${p.projectNumber || ''} ${p.name}`.trim());
    const confirmed = confirm(
      `Delete ${names.length} project${names.length !== 1 ? 's' : ''}?\n\n${names.join('\n')}`
    );
    if (!confirmed) return;
    for (const id of deleteSelected) {
      await ProjectService.delete(id);
    }
    setDeleteMode(false);
    setDeleteSelected(new Set());
    await loadData();
    toast(`Deleted ${names.length} project${names.length !== 1 ? 's' : ''}`);
  }

  // ─── Project forma (LP-3 Brick 1: ONE forma per project) ───
  const [formaPickerId, setFormaPickerId] = useState('');
  const [placingForma, setPlacingForma] = useState(false);

  async function refreshFormas() {
    try {
      setFormas(await FormaService.list());
    } catch {
      /* keep current list */
    }
  }

  async function placeFormaFromMaster(masterId: string) {
    if (!editing?.id || !masterId || placingForma) return;
    const master = formas.find((f) => f.id === masterId);
    if (!master) return;
    setPlacingForma(true);
    try {
      await FormaService.ensureProjectCopy(master, {
        id: editing.id,
        number: editing.projectNumber,
        name: editing.name,
      });
      await refreshFormas();
      setFormaPickerId('');
      toast('Forma placed in project');
    } catch (err: any) {
      toast('Failed to place forma: ' + (err.message || 'Unknown error'), true);
    } finally {
      setPlacingForma(false);
    }
  }

  async function removeProjectForma(f: StandardConfig) {
    const name = f.docSettings?.standardName || 'this forma';
    if (
      !confirm(
        `Remove "${name}" from this project?\n\nThe master it was forked from is untouched. Exports fall back to no forma until you place another.`
      )
    )
      return;
    try {
      await FormaService.delete(f.id);
      await refreshFormas();
      toast('Project forma removed');
    } catch (err: any) {
      toast('Failed to remove forma: ' + (err.message || 'Unknown error'), true);
    }
  }

  // Deliverable CRUD — shared by the hub-table popup and the Deliverables
  // dashboard widget (via widgetCtx). A deliverable is name + due date +
  // project; phase is never required (2026-07-20 divorce).
  async function saveDeliverableForEditing(d: Deliverable) {
    try {
      await TimeService.saveDeliverable(d);
      if (editing?.id) setDeliverables(await TimeService.listDeliverables(editing.id));
      await loadData();
      if (!d.id) toast('Deliverable added');
    } catch (err: any) {
      console.error('saveDeliverable error:', err);
      toast('Failed to save deliverable: ' + (err.message || 'Unknown error'), true);
    }
  }

  async function deleteDeliverable(id: string) {
    if (!editing?.id) return;
    await TimeService.deleteDeliverable(id);
    setDeliverables(await TimeService.listDeliverables(editing.id));
    await loadData();
  }

  async function updateDeliverable(d: Deliverable, patch: Partial<Deliverable>) {
    try {
      await TimeService.saveDeliverable({ ...d, ...patch });
      if (editing?.id) setDeliverables(await TimeService.listDeliverables(editing.id));
      await loadData();
    } catch (err: any) {
      console.error('updateDeliverable error:', err);
      toast('Failed to update deliverable: ' + (err.message || ''), true);
    }
  }

  // Import raw .docx files straight into THIS project's specs (bypasses the
  // firm masters) — for firms mid-migration that already have Word specs.
  // Reuses the harvester (same engine as the Spec Masters import).
  async function handleImportDocxToProject() {
    if (!editing?.id) return;
    const api = (window as any).api;
    if (!api?.openDocxDialog || !api?.extractDocx) {
      toast('Import requires the desktop app', true);
      return;
    }
    const paths: string[] = await api.openDocxDialog();
    if (!paths?.length) return;
    setImportingDocx(true);
    let imported = 0;
    const now = new Date().toISOString();
    for (const p of paths) {
      const fname = p.split(/[/\\]/).pop() || p;
      try {
        const result = await api.extractDocx(p);
        if (!result?.success) {
          toast(`Failed: ${fname} — ${result?.error || 'could not parse'}`, true);
          continue;
        }
        const coreContent = (result.blocks || []).map((b: any, i: number) => ({
          id: 'b-' + Date.now().toString(36) + '-' + i,
          level: b.level,
          text: b.text,
          bold: b.bold,
          italic: b.italic,
          underline: b.underline,
          blockType: b.blockType,
          tableData: b.tableData,
          imageData: b.imageData,
          type: 'fixed' as const,
        }));
        const spec = {
          id: 'ps-' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8),
          projectId: editing.id,
          masterId: '',
          masterVersion: 0,
          csiNumber: result.csiNumber || '',
          title: result.title || fname.replace(/\.docx$/i, ''),
          coreContent,
          systems: [],
          variants: [],
          compiledAt: now,
          lastEdited: now,
        } as ProjectSpec;
        await ProjectSpecService.save(spec);
        imported++;
      } catch (err: any) {
        toast(`Error importing ${fname}: ${err.message || err}`, true);
      }
    }
    try {
      setProjectSpecs(await ProjectSpecService.listByProject(editing.id));
    } catch {}
    setImportingDocx(false);
    if (imported > 0)
      toast(`Imported ${imported} spec${imported !== 1 ? 's' : ''} into ${editing.name}`);
  }

  function statusBadge(s: string) {
    if (s === 'active') return { bg: 'rgba(var(--okr),0.12)', color: 'var(--ok)', text: 'Active' };
    if (s === 'closed') return { bg: 'var(--sa)', color: 'var(--tm)', text: 'Closed' };
    return { bg: 'var(--sa)', color: 'var(--tm)', text: s || 'Unknown' };
  }

  // ─── Inline cell edit handler ───
  async function handleCellEdit(item: Project, key: string, value: any) {
    try {
      await ProjectService.save({ ...item, [key]: value });
      await loadData();
    } catch (err: any) {
      toast('Update failed: ' + (err.message || ''), true);
    }
  }

  // ─── Paste handler ───
  async function handlePaste(rows: Record<string, string>[]) {
    let created = 0;
    for (const row of rows) {
      if (!row.name && !row.projectNumber) continue;
      const proj: Project = {
        id: '',
        name: row.name || '',
        projectNumber: row.projectNumber || '',
        clientName: row.client || '',
        status: (row.status as any) || 'active',
        selections: {},
        createdAt: '',
        updatedAt: '',
      };
      if (row.client) {
        const match = orgs.find(
          (o) =>
            o.name.toLowerCase() === row.client.toLowerCase() ||
            o.shortName?.toLowerCase() === row.client.toLowerCase()
        );
        if (match) proj.clientOrgId = match.id;
        else proj.clientName = row.client;
      }
      try {
        await ProjectService.save(proj);
        created++;
      } catch {}
    }
    if (created > 0) {
      await loadData();
      toast(`Imported ${created} project${created !== 1 ? 's' : ''} from clipboard`);
    }
  }

  // ─── Deliverables inline cell content ───
  function renderDelivCell(p: Project) {
    const delivs = getProjectDeliverables(p);
    return (
      <div style={{ position: 'relative' }} className="deliv-cell">
        {delivs.length === 0 ? (
          <span style={{ color: 'var(--tm)' }}>{'\u2013'}</span>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {delivs.slice(0, 3).map((d) => {
              const dateText = d.dueDate
                ? new Date(d.dueDate).toLocaleDateString(undefined, {
                    month: 'numeric',
                    day: 'numeric',
                    year: '2-digit',
                  })
                : null;
              return (
                <div
                  key={d.id}
                  style={{ display: 'flex', gap: 6, alignItems: 'center', lineHeight: 1.3 }}
                >
                  <span
                    style={{
                      fontWeight: 500,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    {d.name}
                  </span>
                  {dateText && (
                    <span
                      style={{
                        color: 'var(--tm)',
                        fontSize: 11,
                        fontFamily: "'JetBrains Mono', monospace",
                        flexShrink: 0,
                      }}
                    >
                      {dateText}
                    </span>
                  )}
                  {d.issued && (
                    <span style={{ color: 'var(--ok)', flexShrink: 0 }}>{'\u2713'}</span>
                  )}
                </div>
              );
            })}
            {delivs.length > 3 && (
              <span style={{ color: 'var(--tm)', fontSize: 11 }}>+{delivs.length - 3} more</span>
            )}
          </div>
        )}
        <button
          className="deliv-hover-btn"
          onClick={(e) => {
            e.stopPropagation();
            openDeliverables(p);
          }}
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            padding: '2px 8px',
            borderRadius: 4,
            border: '1px solid var(--bd)',
            background: 'var(--sf)',
            color: 'var(--ac)',
            fontSize: 10,
            fontWeight: 600,
            cursor: 'pointer',
            display: 'none',
            whiteSpace: 'nowrap',
            zIndex: 2,
          }}
        >
          Add/Edit
        </button>
        <style>{`.deliv-cell:hover .deliv-hover-btn { display: block !important; }`}</style>
      </div>
    );
  }

  function openDeliverables(proj: Project) {
    setEditing(JSON.parse(JSON.stringify(proj)));
    setDirty(false);
    setEditMode(true);
    setDetailView(true);
    if (proj.id) loadProjectDetails(proj.id);
  }

  function renderDelivEditCell(p: Project) {
    if (!p.id)
      return (
        <span style={{ color: 'var(--tm)', fontSize: 10, fontStyle: 'italic' }}>
          Save project first
        </span>
      );
    const delivs = getProjectDeliverables(p, { includeIssued: true });
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 200 }}>
        {delivs.map((d) => (
          <div key={d.id} style={{ display: 'flex', gap: 3, alignItems: 'center', fontSize: 10 }}>
            <input
              type="text"
              defaultValue={d.name}
              onBlur={(e) => {
                if (e.target.value !== d.name) updateDeliverable(d, { name: e.target.value });
              }}
              style={{ ...miniInputStyle, flex: 1, minWidth: 60 }}
              placeholder="Name"
            />
            <input
              type="date"
              defaultValue={d.dueDate || ''}
              onChange={(e) => updateDeliverable(d, { dueDate: e.target.value || undefined })}
              style={{ ...miniInputStyle, width: 105, fontSize: 11 }}
              title="Due Date"
            />
            <input
              type="date"
              defaultValue={d.sealDate || ''}
              onChange={(e) => updateDeliverable(d, { sealDate: e.target.value || undefined })}
              style={{ ...miniInputStyle, width: 105, fontSize: 11 }}
              title="Seal Date"
            />
            <button
              type="button"
              disabled={!d.dueDate || d.sealDate === d.dueDate}
              onClick={() => d.dueDate && updateDeliverable(d, { sealDate: d.dueDate })}
              title={
                d.dueDate
                  ? d.sealDate === d.dueDate
                    ? 'Seal date already matches due date'
                    : 'Set seal date = due date'
                  : 'Set a due date first'
              }
              style={{
                background: 'transparent',
                border: '1px solid var(--bd)',
                borderRadius: 3,
                padding: '1px 4px',
                fontSize: 10,
                fontWeight: 700,
                color: !d.dueDate || d.sealDate === d.dueDate ? 'var(--bd)' : 'var(--ac)',
                cursor: !d.dueDate || d.sealDate === d.dueDate ? 'default' : 'pointer',
                lineHeight: 1.2,
              }}
            >
              = DUE
            </button>
            <input
              type="checkbox"
              checked={!!d.issued}
              onChange={(e) => updateDeliverable(d, { issued: e.target.checked })}
              style={{ cursor: 'pointer' }}
              title="Issued"
            />
            <button
              onClick={() => {
                TimeService.deleteDeliverable(d.id).then(() => loadData());
              }}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--dg)',
                cursor: 'pointer',
                fontSize: 10,
                padding: 0,
              }}
              title="Delete"
            >
              {'\u2715'}
            </button>
          </div>
        ))}
        <div style={{ display: 'flex', gap: 3, alignItems: 'center', fontSize: 10, opacity: 0.7 }}>
          <input
            type="text"
            placeholder="+ Add deliverable..."
            style={{ ...miniInputStyle, flex: 1, minWidth: 60, fontStyle: 'italic' }}
            onKeyDown={async (e) => {
              if (e.key === 'Enter' && (e.target as HTMLInputElement).value.trim()) {
                try {
                  await TimeService.saveDeliverable({
                    id: '',
                    projectId: p.id,
                    name: (e.target as HTMLInputElement).value.trim(),
                    description: '',
                    status: 'pending',
                    issued: false,
                    createdAt: '',
                    updatedAt: '',
                  });
                  (e.target as HTMLInputElement).value = '';
                  await loadData();
                } catch (err: any) {
                  toast('Failed to add deliverable: ' + (err.message || ''), true);
                }
              }
            }}
          />
        </div>
      </div>
    );
  }

  // Count of project specs composed into each project — drives the "Specs"
  // hub column. Re-derives whenever the firm-wide spec list refreshes.
  const specCountByProject = useMemo(() => {
    const m = new Map<string, number>();
    for (const s of allProjectSpecs) {
      if (!s.projectId) continue;
      m.set(s.projectId, (m.get(s.projectId) || 0) + 1);
    }
    return m;
  }, [allProjectSpecs]);

  // ─── DataTable columns ───
  const columns = useMemo<Column<Project>[]>(
    () => [
      {
        key: 'projectNumber',
        label: 'Project #',
        width: 120,
        minWidth: 80,
        editable: false,
        editType: 'text',
        editKey: 'projectNumber',
        getValue: (p) => p.projectNumber || '',
        render: (p) => (
          <span
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              color: 'var(--ac)',
              fontWeight: 600,
            }}
          >
            {p.projectNumber || '\u2013'}
          </span>
        ),
      },
      {
        key: 'name',
        label: 'Name',
        width: 200,
        minWidth: 120,
        editable: false,
        editType: 'text',
        getValue: (p) => p.name || '',
        render: (p) => (
          <span style={{ fontWeight: 600 }}>{sanitizeProjectName(p.name) || 'Untitled'}</span>
        ),
      },
      {
        key: 'client',
        label: 'Client',
        width: 140,
        minWidth: 80,
        editable: false,
        editType: 'select',
        editKey: 'clientOrgId',
        editOptions: orgs.map((o) => ({ value: o.id, label: o.name })),
        getValue: (p) => getClient(p),
        render: (p) => <span style={{ color: 'var(--tm)' }}>{getClient(p) || '\u2013'}</span>,
      },
      {
        key: 'type',
        label: 'Type',
        width: 120,
        minWidth: 80,
        editable: false,
        editType: 'select',
        editKey: 'projectTypeId',
        editOptions: projectTypes.map((t) => ({ value: t.id, label: t.name })),
        getValue: (p) => typeName(p.projectTypeId),
        render: (p) => (
          <span style={{ color: 'var(--tm)' }}>{typeName(p.projectTypeId) || '\u2013'}</span>
        ),
      },
      {
        key: 'billingType',
        label: 'Fee Structure',
        width: 130,
        minWidth: 90,
        editable: false,
        editType: 'select',
        editKey: 'billingTypeId',
        editOptions: billingTypes.map((t) => ({ value: t.id, label: t.name })),
        getValue: (p) => billingName(p.billingTypeId),
        render: (p) => (
          <span style={{ color: 'var(--tm)' }}>{billingName(p.billingTypeId) || '\u2013'}</span>
        ),
      },
      {
        key: 'phase',
        label: 'Phase',
        width: 120,
        minWidth: 80,
        getValue: (p) => getProjectPhase(p),
        render: (p) => {
          const phase = getProjectPhase(p);
          return (
            <span style={{ color: phase ? 'var(--tx)' : 'var(--tm)' }}>{phase || '\u2013'}</span>
          );
        },
        renderEdit: (p) => {
          const projPhases = allPhases
            .filter((ph) => ph.projectId === p.id)
            .sort((a, b) => a.sortOrder - b.sortOrder);
          const current = getProjectPhase(p).replace(' (Done)', '');
          if (projPhases.length === 0)
            return <span style={{ color: 'var(--tm)', fontStyle: 'italic' }}>No phases</span>;
          return (
            <SearchableSelect
              value={current}
              options={projPhases.map((ph) => ({ value: ph.name, label: ph.name }))}
              onChange={(v) => setProjectPhase(p, v)}
              searchPlaceholder="Search phases…"
              clearable={false}
              style={{ width: '100%', padding: '3px 6px' }}
            />
          );
        },
      },
      {
        key: 'deliverables',
        label: 'Deliverables',
        width: 260,
        minWidth: 140,
        sortable: true,
        getValue: (p) => getProjectDeliverables(p).length,
        render: (p) => renderDelivCell(p),
        renderEdit: (p) => renderDelivEditCell(p),
      },
      {
        key: 'specs',
        label: 'Specs',
        width: 90,
        minWidth: 64,
        sortable: true,
        getValue: (p) => specCountByProject.get(p.id) || 0,
        render: (p) => {
          const n = specCountByProject.get(p.id) || 0;
          if (n === 0) return <span style={{ color: 'var(--tm)' }}>{'–'}</span>;
          return (
            <button
              onClick={(e) => {
                e.stopPropagation();
                openEdit(p, 'specs');
              }}
              title={`${n} spec${n === 1 ? '' : 's'} composed — open specs`}
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 11,
                fontWeight: 700,
                padding: '2px 9px',
                borderRadius: 999,
                border: '1px solid rgba(var(--acr),0.30)',
                background: 'rgba(var(--acr),0.10)',
                color: 'var(--ac)',
                cursor: 'pointer',
                lineHeight: 1.4,
              }}
            >
              {n}
            </button>
          );
        },
      },
      {
        key: 'scopes',
        label: 'Scopes',
        width: 140,
        minWidth: 80,
        sortable: true,
        getValue: (p) =>
          getProjectScopes(p)
            .map((s) => s.name)
            .join(', '),
        render: (p) => {
          const ps = getProjectScopes(p);
          if (ps.length === 0) return <span style={{ color: 'var(--tm)' }}>{'\u2013'}</span>;
          return (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              {ps.map((s) => (
                <span
                  key={s.id}
                  style={{
                    fontSize: 10,
                    padding: '1px 6px',
                    borderRadius: 3,
                    background: s.color + '20',
                    color: s.color,
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {s.name}
                </span>
              ))}
            </div>
          );
        },
      },
      {
        key: 'status',
        label: 'Status',
        width: 90,
        minWidth: 70,
        editable: false,
        editType: 'select',
        editKey: 'status',
        editOptions: [
          { value: 'active', label: 'Active' },
          { value: 'closed', label: 'Closed' },
        ],
        getValue: (p) => p.status || 'active',
        render: (p) => {
          const sb = statusBadge(p.status || 'active');
          return (
            <span
              style={{
                fontSize: 10,
                padding: '2px 6px',
                borderRadius: 3,
                background: sb.bg,
                color: sb.color,
                fontWeight: 600,
              }}
            >
              {sb.text}
            </span>
          );
        },
      },
    ],
    [
      orgs,
      projectTypes,
      billingTypes,
      allPhases,
      allDeliverables,
      allScopes,
      allScopeAssignments,
      specCountByProject,
    ]
  );

  // Group options: every column is groupable, per Josh's "full suite on every table" rule.
  // For columns returning complex renders, we still fall back to the column's getValue for
  // the group key — which is why every column defines one.
  const groupOptions = useMemo<GroupOption<Project>[]>(
    () =>
      columns.map((c) => ({
        key: c.key,
        label: `By ${c.label}`,
        getValue: (p: Project) => {
          const v = c.getValue ? c.getValue(p) : (p as any)[c.key];
          return v == null || v === '' ? `No ${c.label}` : String(v);
        },
      })),
    [columns]
  );

  // ─── Read-only field display ───
  function ReadField({ label, value }: { label: string; value: string }) {
    return (
      <div style={{ marginBottom: 10 }}>
        <div style={labelStyle}>{label}</div>
        <div
          style={{
            padding: '7px 10px',
            background: 'var(--sa)',
            borderRadius: 5,
            fontSize: 13,
            color: value ? 'var(--tx)' : 'var(--tm)',
            minHeight: 34,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {value || '\u2013'}
        </div>
      </div>
    );
  }

  const isNew = !editing?.id;

  // ─── Team assignment handlers ───

  /**
   * Keep project_assignments in sync with the project's picId/pmId fields.
   * - The project record is the source of truth for who is PIC / PM.
   * - This mirrors them as role='pic' / role='pm' assignment rows so they
   *   appear in the Project Team section alongside other members.
   * - Changing PIC/PM on the project will demote the previous person (their
   *   assignment drops to role='member' so they stay on the team) and promote
   *   the new person. Clearing PIC/PM demotes the current holder to member.
   */
  async function syncPicPmAssignments(projectId: string, picId?: string, pmId?: string) {
    const current = await TeamService.listAssignments(undefined, projectId);
    const byRole = (role: string) => current.filter((a) => a.role === role);

    async function syncRole(role: 'pic' | 'pm', targetMemberId: string | undefined) {
      const holders = byRole(role);
      // Demote anyone currently in this role who isn't the target
      for (const h of holders) {
        if (h.teamMemberId !== targetMemberId) {
          // Demote to member rather than remove, so they remain on the team.
          await TeamService.assign(projectId, h.teamMemberId, 'member');
          // assign() upserts on (project, member) — explicitly delete the old row
          // if a new one was created with a different id.
          const refreshed = await TeamService.listAssignments(undefined, projectId);
          const stillOld = refreshed.find((a) => a.id === h.id && a.role === role);
          if (stillOld) await TeamService.unassign(stillOld.id);
        }
      }
      // Promote (or create) the target
      if (targetMemberId) {
        const existing = current.find((a) => a.teamMemberId === targetMemberId);
        if (existing) {
          if (existing.role !== role) {
            await TeamService.assign(projectId, targetMemberId, role);
            // Clean up old role row if assign created a new one
            const refreshed = await TeamService.listAssignments(undefined, projectId);
            const dups = refreshed.filter((a) => a.teamMemberId === targetMemberId);
            if (dups.length > 1) {
              for (const d of dups.slice(1)) await TeamService.unassign(d.id);
            }
          }
        } else {
          await TeamService.assign(projectId, targetMemberId, role);
        }
      }
    }

    await syncRole('pic', picId);
    await syncRole('pm', pmId);

    // Refresh local assignment state so the team panel re-renders immediately
    setProjectAssignments(await TeamService.listAssignments(undefined, projectId));
  }

  async function assignMemberToProject(memberId: string) {
    if (!editing?.id) return;
    // Prevent duplicate assignment
    if (projectAssignments.some((a) => a.teamMemberId === memberId)) {
      toast('Member already assigned to this project', true);
      return;
    }
    try {
      await TeamService.assign(editing.id, memberId, 'member');
      setProjectAssignments(await TeamService.listAssignments(undefined, editing.id));
      toast('Member assigned');
    } catch (err: any) {
      toast('Failed to assign member: ' + (err.message || ''), true);
    }
  }

  async function unassignMemberFromProject(assignmentId: string) {
    if (!editing?.id) return;
    const a = projectAssignments.find((x) => x.id === assignmentId);
    try {
      await TeamService.unassign(assignmentId);
      // If the removed member was PIC or PM on the project record, clear those fields
      if (a && editing) {
        const clearPic = editing.picId === a.teamMemberId;
        const clearPm = editing.pmId === a.teamMemberId;
        if (clearPic || clearPm) {
          const updated = {
            ...editing,
            picId: clearPic ? undefined : editing.picId,
            pmId: clearPm ? undefined : editing.pmId,
          };
          await ProjectService.save(updated);
          setEditing((prev) =>
            prev ? { ...prev, picId: updated.picId, pmId: updated.pmId } : prev
          );
        }
      }
      setProjectAssignments(await TeamService.listAssignments(undefined, editing.id));
      toast('Member removed');
    } catch (err: any) {
      toast('Failed to remove member: ' + (err.message || ''), true);
    }
  }

  async function changeAssignmentRole(assignmentId: string, newRole: string) {
    if (!editing?.id) return;
    const a = projectAssignments.find((x) => x.id === assignmentId);
    if (!a) return;
    try {
      await TeamService.assign(a.projectId, a.teamMemberId, newRole);
      // assign creates new if not existing — need to unassign old + re-fetch
      await TeamService.unassign(assignmentId);
      setProjectAssignments(await TeamService.listAssignments(undefined, editing.id));

      // Mirror onto the project record when user picks PIC/PM here.
      // This keeps the Project Details card in sync with the team panel.
      if (newRole === 'pic' || newRole === 'pm') {
        const field = newRole === 'pic' ? 'picId' : 'pmId';
        const currentHolder = (editing as any)[field];
        if (currentHolder !== a.teamMemberId) {
          const updated = { ...editing, [field]: a.teamMemberId };
          await ProjectService.save(updated);
          setEditing((prev) => (prev ? { ...prev, [field]: a.teamMemberId } : prev));
        }
      } else if (newRole === 'member') {
        // Demoting from PIC/PM via the team panel — clear the project field
        if (editing.picId === a.teamMemberId) {
          const updated = { ...editing, picId: undefined as string | undefined };
          await ProjectService.save(updated);
          setEditing((prev) => (prev ? { ...prev, picId: undefined as string | undefined } : prev));
        }
        if (editing.pmId === a.teamMemberId) {
          const updated = { ...editing, pmId: undefined as string | undefined };
          await ProjectService.save(updated);
          setEditing((prev) => (prev ? { ...prev, pmId: undefined as string | undefined } : prev));
        }
      }
    } catch (err: any) {
      toast('Failed to change role: ' + (err.message || ''), true);
    }
  }

  async function assignTeamToProject(teamId: string) {
    if (!editing?.id) return;
    try {
      const tas = await TeamService.listTeamAssignments(teamId);
      let added = 0;
      for (const ta of tas) {
        if (!projectAssignments.some((a) => a.teamMemberId === ta.teamMemberId)) {
          await TeamService.assign(editing.id, ta.teamMemberId, 'member');
          added++;
        }
      }
      setProjectAssignments(await TeamService.listAssignments(undefined, editing.id));
      setShowTeamPicker(false);
      const team = teams.find((t) => t.id === teamId);
      toast(`Assigned ${added} member${added !== 1 ? 's' : ''} from ${team?.name || 'team'}`);
    } catch (err: any) {
      toast('Failed to assign team: ' + (err.message || ''), true);
    }
  }

  // Computed: members available for assignment (not already assigned)
  const assignedMemberIdsForProject = new Set(projectAssignments.map((a) => a.teamMemberId));
  const availableMembersForProject = members.filter(
    (m) => m.active && !assignedMemberIdsForProject.has(m.id)
  );

  // ═══════════ FULL-PAGE PROJECT DETAIL ═══════════
  if (detailView && editing) {
    // Widget render helpers — each returns the JSX for one section of the
    // project detail. They close over the component's state/handlers, so
    // they're defined inline here rather than hoisted out. Headings have been
    // removed from the bodies because WidgetGrid provides its own titles.
    const detailsBody = editMode ? (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
          gap: '12px 20px',
        }}
      >
        <label>
          <div style={labelStyle}>Project Number</div>
          <input
            value={editing.projectNumber || ''}
            onChange={(e) => update({ projectNumber: e.target.value })}
            placeholder="e.g. 2026-001"
            style={{ ...inputStyle, fontFamily: "'JetBrains Mono', monospace" }}
          />
        </label>
        <div>
          <div style={labelStyle}>Status</div>
          <SearchableSelect
            value={editing.status || 'active'}
            options={[
              { value: 'active', label: 'Active' },
              { value: 'closed', label: 'Closed' },
            ]}
            onChange={(v) => update({ status: (v || 'active') as any })}
            placeholder="Select status"
            clearable={false}
          />
        </div>
        <label style={{ gridColumn: '1 / -1' }}>
          <div style={labelStyle}>Project Name</div>
          <input
            value={editing.name}
            onChange={(e) => update({ name: e.target.value })}
            style={inputStyle}
            autoFocus
          />
        </label>
        <label style={{ gridColumn: '1 / -1' }}>
          <div style={labelStyle}>Project Tokens</div>
          {/* Replaces the old "Client Project Name" field: user-defined lines
              for this project's formas (free text + data inserts). Legacy
              clientProjectName still resolves for old formas. */}
          <ProjectFormaTokens
            tokens={((editing as any).formaTokens || []) as any}
            onChange={(next) => update({ formaTokens: next } as any)}
          />
        </label>
        <div>
          <div style={labelStyle}>Client</div>
          <SearchableSelect
            value={editing.clientOrgId || ''}
            options={(() => {
              const clients = orgs.filter((o) => o.type === 'client' || o.type === 'both');
              const others = orgs.filter((o) => o.type !== 'client' && o.type !== 'both');
              return [
                ...clients.map((o) => ({ value: o.id, label: o.name, group: 'Clients' })),
                ...others.map((o) => ({ value: o.id, label: o.name, group: 'Other' })),
              ];
            })()}
            onChange={(v) =>
              update({
                clientOrgId: v || undefined,
                clientName: orgs.find((o) => o.id === v)?.name,
              })
            }
            placeholder="Select client"
            searchPlaceholder="Search clients…"
          />
        </div>
        <div>
          <div style={labelStyle}>Project Type</div>
          <SearchableSelect
            value={editing.projectTypeId || ''}
            options={projectTypes.map((t) => ({ value: t.id, label: t.name }))}
            onChange={(v) => update({ projectTypeId: v || undefined })}
            placeholder="Select type"
            searchPlaceholder="Search types…"
          />
        </div>
        <div>
          <div style={labelStyle}>Fee Structure</div>
          <SearchableSelect
            value={editing.billingTypeId || ''}
            options={billingTypes.map((t) => ({ value: t.id, label: t.name }))}
            onChange={(v) => update({ billingTypeId: v || undefined })}
            placeholder={
              billingTypes.length === 0 ? 'Add fee structures in Admin' : 'Select fee structure'
            }
            searchPlaceholder="Search fee structures…"
          />
        </div>
        <div>
          <div style={labelStyle}>PIC (Principal-in-Charge)</div>
          <SearchableSelect
            value={editing.picId || ''}
            options={members
              .filter((m) => m.active)
              .map((m) => ({ value: m.id, label: m.name, hint: m.role }))}
            onChange={(v) => update({ picId: v || undefined })}
            placeholder="Select PIC"
            searchPlaceholder="Search team…"
          />
        </div>
        <div>
          <div style={labelStyle}>PM (Project Manager)</div>
          <SearchableSelect
            value={editing.pmId || ''}
            options={members
              .filter((m) => m.active)
              .map((m) => ({ value: m.id, label: m.name, hint: m.role }))}
            onChange={(v) => update({ pmId: v || undefined })}
            placeholder="Select PM"
            searchPlaceholder="Search team…"
          />
        </div>
        <label>
          <div style={labelStyle}>Contract Value</div>
          <input
            type="number"
            value={editing.contractValue ?? ''}
            onChange={(e) =>
              update({ contractValue: e.target.value ? Number(e.target.value) : undefined })
            }
            placeholder="0.00"
            style={{ ...inputStyle, fontFamily: "'JetBrains Mono', monospace" }}
          />
        </label>
        <label>
          <div style={labelStyle}>Anticipated Hours</div>
          <input
            type="number"
            value={editing.anticipatedHours ?? ''}
            onChange={(e) =>
              update({ anticipatedHours: e.target.value ? Number(e.target.value) : undefined })
            }
            placeholder="0"
            style={{ ...inputStyle, fontFamily: "'JetBrains Mono', monospace" }}
          />
        </label>
        {editing.id && phases.length > 0 && (
          <div>
            <div style={labelStyle}>Current Phase</div>
            <SearchableSelect
              value={(() => {
                const inProg = phases.filter((p) => p.status === 'in-progress');
                if (inProg.length > 0) return inProg[inProg.length - 1].name;
                const pend = phases.filter((p) => p.status === 'pending');
                if (pend.length > 0) return pend[0].name;
                return '';
              })()}
              options={phases
                .slice()
                .sort((a, b) => a.sortOrder - b.sortOrder)
                .map((ph) => ({ value: ph.name, label: ph.name, hint: ph.status }))}
              onChange={async (v) => {
                if (!v) return;
                await setProjectPhase(editing, v);
                if (editing.id) await syncAndLoadPhases(editing.id);
              }}
              placeholder="Select phase"
              clearable={false}
            />
          </div>
        )}
        {!isWeb && (
          <div style={{ gridColumn: '1 / -1' }}>
            <div style={labelStyle}>Project Folder</div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input
                value={editing.folderPath || ''}
                readOnly
                placeholder="No folder linked"
                title={editing.folderPath || ''}
                style={{
                  ...inputStyle,
                  flex: 1,
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 11,
                }}
              />
              <button
                onClick={async () => {
                  const dir = await (window as any).api?.selectFolderDialog?.();
                  if (dir) update({ folderPath: dir });
                }}
                style={{
                  padding: '6px 12px',
                  borderRadius: 5,
                  border: '1px solid var(--bd)',
                  background: 'var(--sf)',
                  color: 'var(--tx)',
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Pick
              </button>
              {editing.folderPath && (
                <button
                  onClick={() => (window as any).api?.openFolder?.(editing.folderPath)}
                  style={{
                    padding: '6px 12px',
                    borderRadius: 5,
                    border: '1px solid var(--ac)',
                    background: 'transparent',
                    color: 'var(--ac)',
                    fontSize: 11,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Open
                </button>
              )}
              {editing.folderPath && (
                <button
                  onClick={() => update({ folderPath: undefined })}
                  style={{
                    padding: '6px 10px',
                    borderRadius: 5,
                    border: '1px solid var(--bd)',
                    background: 'transparent',
                    color: 'var(--tm)',
                    fontSize: 11,
                    cursor: 'pointer',
                  }}
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        )}
        <label style={{ gridColumn: '1 / -1' }}>
          <div style={labelStyle}>Description</div>
          <textarea
            value={editing.description || ''}
            onChange={(e) => update({ description: e.target.value })}
            rows={4}
            style={{ ...inputStyle, resize: 'vertical' }}
          />
        </label>
        {editing.id && allScopes.length > 0 && (
          <div style={{ gridColumn: '1 / -1', marginTop: 4 }}>
            <div style={labelStyle}>Scopes</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {allScopes
                .filter((s) => !s.parentId)
                .map((parent) => {
                  const children = allScopes.filter((s) => s.parentId === parent.id);
                  const items = children.length > 0 ? children : [parent];
                  const showParentCheckbox = children.length === 0;
                  return (
                    <div key={parent.id}>
                      {children.length > 0 && (
                        <div
                          style={{
                            fontSize: 11,
                            fontWeight: 700,
                            color: 'var(--tm)',
                            marginBottom: 4,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                          }}
                        >
                          <span
                            style={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              background: parent.color,
                              flexShrink: 0,
                            }}
                          />
                          {parent.name}
                        </div>
                      )}
                      <div
                        style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 6,
                          marginLeft: children.length > 0 ? 14 : 0,
                        }}
                      >
                        {(showParentCheckbox ? [parent] : items).map((scope) => {
                          const assigned = dialogScopeAssignments.some(
                            (a) => a.scopeId === scope.id
                          );
                          return (
                            <label
                              key={scope.id}
                              onClick={() => toggleScope(scope.id)}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6,
                                cursor: 'pointer',
                                fontSize: 13,
                                padding: '4px 10px',
                                borderRadius: 5,
                                background: assigned ? scope.color + '18' : 'var(--sa)',
                                border: `1px solid ${assigned ? scope.color + '40' : 'var(--bd)'}`,
                                transition: 'all .15s',
                              }}
                            >
                              <div
                                style={{
                                  width: 16,
                                  height: 16,
                                  borderRadius: 3,
                                  flexShrink: 0,
                                  border: `2px solid ${assigned ? scope.color : 'var(--bd)'}`,
                                  background: assigned ? scope.color : 'transparent',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: 'var(--color-text-inverse)',
                                  fontSize: 10,
                                }}
                              >
                                {assigned ? '\u2713' : ''}
                              </div>
                              <span
                                style={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  background: scope.color,
                                  flexShrink: 0,
                                }}
                              />
                              <span
                                style={{
                                  fontWeight: assigned ? 600 : 400,
                                  color: assigned ? scope.color : 'var(--tx)',
                                }}
                              >
                                {scope.name}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        )}
        {/* Edit-mode save/cancel — lives on the widget itself */}
        <div
          style={{
            gridColumn: '1 / -1',
            marginTop: 8,
            display: 'flex',
            gap: 8,
            justifyContent: 'flex-end',
          }}
        >
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              if (isNew) closeDetail();
              else {
                setEditMode(false);
                setEditing(
                  JSON.parse(JSON.stringify(projects.find((p) => p.id === editing.id) || editing))
                );
                setDirty(false);
              }
            }}
          >
            Cancel
          </Button>
          <Button size="sm" onClick={saveProject} style={{ opacity: dirty ? 1 : 0.7 }}>
            Save{dirty ? ' *' : ''}
          </Button>
        </div>
      </div>
    ) : !isNew ? (
      <div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: '8px 20px',
          }}
        >
          <ReadField label="Project Number" value={editing.projectNumber || ''} />
          <ReadField
            label="Status"
            value={
              (editing.status || 'active').charAt(0).toUpperCase() +
              (editing.status || 'active').slice(1)
            }
          />
          <div style={{ gridColumn: '1 / -1' }}>
            <ReadField label="Project Name" value={sanitizeProjectName(editing.name)} />
          </div>
          {(((editing as any).formaTokens || []) as any[]).length > 0 && (
            <div style={{ gridColumn: '1 / -1' }}>
              <ReadField
                label="Project Tokens"
                value={(((editing as any).formaTokens || []) as any[])
                  .map((t: any) => t.name || '(unnamed)')
                  .join(' · ')}
              />
            </div>
          )}
          <ReadField label="Client" value={getClient(editing)} />
          <ReadField label="Project Type" value={typeName(editing.projectTypeId)} />
          <ReadField label="Fee Structure" value={billingName(editing.billingTypeId)} />
          <ReadField
            label="PIC"
            value={members.find((m) => m.id === editing.picId)?.name || '\u2013'}
          />
          <ReadField
            label="PM"
            value={members.find((m) => m.id === editing.pmId)?.name || '\u2013'}
          />
          {editing.contractValue != null && (
            <ReadField
              label="Contract Value"
              value={`$${editing.contractValue.toLocaleString()}`}
            />
          )}
          {editing.anticipatedHours != null && (
            <ReadField
              label="Anticipated Hours"
              value={editing.anticipatedHours.toLocaleString()}
            />
          )}
          {editing.description && (
            <div style={{ gridColumn: '1 / -1' }}>
              <ReadField label="Description" value={editing.description} />
            </div>
          )}
          {dialogScopeAssignments.length > 0 && (
            <div style={{ gridColumn: '1 / -1', marginTop: 4 }}>
              <div style={labelStyle}>Scopes</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {Array.from(
                  new Map(dialogScopeAssignments.map((a) => [a.scopeId, a])).values()
                ).map((a) => {
                  const scope = allScopes.find((s) => s.id === a.scopeId);
                  if (!scope) return null;
                  return (
                    <span
                      key={a.id}
                      style={{
                        fontSize: 11,
                        padding: '3px 10px',
                        borderRadius: 4,
                        background: scope.color + '18',
                        color: scope.color,
                        fontWeight: 600,
                      }}
                    >
                      {scope.name}
                    </span>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        {/* Edit button — lives on the widget, not the global header */}
        {canEditProject && (
          <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid var(--bd)' }}>
            <button
              onClick={() => {
                setEditMode(true);
                setDirty(false);
              }}
              style={{
                padding: '5px 14px',
                borderRadius: 5,
                border: '1px solid var(--ac)',
                background: 'transparent',
                color: 'var(--ac)',
                fontSize: 11,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Edit Details
            </button>
          </div>
        )}
      </div>
    ) : (
      <div style={{ fontSize: 13, color: 'var(--tm)', fontStyle: 'italic' }}>
        Save the project to see its details here.
      </div>
    );

    // Compact details — key facts in a tight layout for 1-col width, includes scopes + edit button.
    const detailsCompactBody =
      !isNew && editing ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13 }}>
          {editing.projectNumber && (
            <div>
              <span style={{ color: 'var(--tm)', fontSize: 10, fontWeight: 600 }}>No.</span>{' '}
              <span
                style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontWeight: 700,
                  color: 'var(--ac)',
                }}
              >
                {editing.projectNumber}
              </span>
            </div>
          )}
          <div>
            <span style={{ color: 'var(--tm)', fontSize: 10, fontWeight: 600 }}>Client</span>{' '}
            {getClient(editing) || '\u2013'}
          </div>
          <div>
            <span style={{ color: 'var(--tm)', fontSize: 10, fontWeight: 600 }}>Type</span>{' '}
            {typeName(editing.projectTypeId) || '\u2013'}
          </div>
          <div>
            <span style={{ color: 'var(--tm)', fontSize: 10, fontWeight: 600 }}>PIC</span>{' '}
            {members.find((m) => m.id === editing.picId)?.name || '\u2013'}
          </div>
          <div>
            <span style={{ color: 'var(--tm)', fontSize: 10, fontWeight: 600 }}>PM</span>{' '}
            {members.find((m) => m.id === editing.pmId)?.name || '\u2013'}
          </div>
          {editing.contractValue != null && (
            <div>
              <span style={{ color: 'var(--tm)', fontSize: 10, fontWeight: 600 }}>Value</span> $
              {editing.contractValue.toLocaleString()}
            </div>
          )}
          {dialogScopeAssignments.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, marginTop: 2 }}>
              {Array.from(new Map(dialogScopeAssignments.map((a) => [a.scopeId, a])).values()).map(
                (a) => {
                  const scope = allScopes.find((s) => s.id === a.scopeId);
                  if (!scope) return null;
                  return (
                    <span
                      key={a.id}
                      style={{
                        fontSize: 10,
                        padding: '1px 6px',
                        borderRadius: 3,
                        background: scope.color + '18',
                        color: scope.color,
                        fontWeight: 600,
                      }}
                    >
                      {scope.name}
                    </span>
                  );
                }
              )}
            </div>
          )}
          {editMode ? (
            <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
              <span style={{ fontSize: 10, color: 'var(--ac)', fontStyle: 'italic', flex: 1 }}>
                Expand to edit fields
              </span>
              <button
                onClick={() => {
                  if (isNew) closeDetail();
                  else {
                    setEditMode(false);
                    setEditing(
                      JSON.parse(
                        JSON.stringify(projects.find((p) => p.id === editing.id) || editing)
                      )
                    );
                    setDirty(false);
                  }
                }}
                style={{
                  padding: '3px 8px',
                  borderRadius: 4,
                  border: '1px solid var(--bd)',
                  background: 'transparent',
                  color: 'var(--tm)',
                  fontSize: 10,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={saveProject}
                style={{
                  padding: '3px 8px',
                  borderRadius: 4,
                  border: 'none',
                  background: 'var(--ac)',
                  color: 'var(--color-text-inverse)',
                  fontSize: 10,
                  fontWeight: 600,
                  cursor: 'pointer',
                  opacity: dirty ? 1 : 0.7,
                }}
              >
                Save{dirty ? ' *' : ''}
              </button>
            </div>
          ) : (
            canEditProject && (
              <button
                onClick={() => {
                  setEditMode(true);
                  setDirty(false);
                }}
                style={{
                  padding: '3px 8px',
                  borderRadius: 4,
                  border: '1px solid var(--ac)',
                  background: 'transparent',
                  color: 'var(--ac)',
                  fontSize: 10,
                  fontWeight: 600,
                  cursor: 'pointer',
                  alignSelf: 'flex-start',
                  marginTop: 4,
                }}
              >
                Edit
              </button>
            )
          )}
        </div>
      ) : (
        detailsBody
      );

    // Drill-through router: each interactive widget element calls
    // onDrill with a typed target; we route to the right place.
    //
    // Two classes of drill:
    //   1. Cross-page (invoice, time-entry list, forecast week, team
    //      member): use the parent shell's `navigate` to switch to the
    //      target page with state preloaded.
    //   2. In-page (phase, budget editor, deliverable, task): scroll
    //      the relevant widget into view and (for budget) auto-pulse
    //      the Edit affordance via a brief highlight on the widget.
    const scrollToWidget = (id: string) => {
      const el = document.querySelector(`[data-widget-id="${id}"]`) as HTMLElement | null;
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // Brief outline pulse so the user sees where they landed — no flicker.
      const prev = el.style.boxShadow;
      el.style.boxShadow = '0 0 0 2px rgba(99,102,241,0.55)';
      setTimeout(() => {
        el.style.boxShadow = prev;
      }, 1200);
    };

    const handleDrill = (target: DashboardDrill) => {
      switch (target.kind) {
        case 'tab':
          setProjectDetailTab(target.tab);
          return;
        case 'widget-focus':
          scrollToWidget(target.widgetId);
          return;
        // ─── In-page drills — open inline editors / scroll + highlight ──
        case 'budget-editor':
          scrollToWidget(target.phaseId ? 'phase_health' : 'budget_hours');
          return;
        case 'phase':
          scrollToWidget('phases');
          return;
        case 'deliverable':
          scrollToWidget('deliverables');
          return;
        case 'task':
        case 'new-task':
          scrollToWidget('risks');
          return;
        // ─── Cross-page drills — navigate the shell with state ──
        case 'time-entry-list':
          if (navigate) {
            navigate({
              view: 'time-expense',
              tab: 'timesheets',
              projectId: editing.id,
              weekStart: target.weekStart,
            });
          } else {
            scrollToWidget('burn_rate');
          }
          return;
        case 'forecast-week':
          if (navigate) {
            navigate({
              view: 'time-expense',
              tab: 'forecasting',
              projectId: editing.id,
              weekStart: target.weekStart,
            });
          } else {
            scrollToWidget('forecast_outlook');
          }
          return;
        case 'invoice':
          if (navigate) {
            navigate({
              view: 'invoices',
              projectId: editing.id,
              invoiceId: target.invoiceId,
            });
          } else {
            scrollToWidget('ar_aging');
          }
          return;
        case 'invoice-list':
          if (navigate) {
            navigate({
              view: 'invoices',
              projectId: editing.id,
              status: target.status || 'all',
            });
          } else {
            scrollToWidget('ar_aging');
          }
          return;
        case 'team-member':
          if (navigate) {
            navigate({ view: 'team', memberId: target.memberId });
          } else {
            scrollToWidget('team_utilization');
          }
          return;
      }
    };

    // Shared context for the financial widgets. Hoisted so the JSX
    // below can reference it without a temporal-dead-zone error.
    // phases/deliverables are page-level live state (reloaded by the
    // specforma-sync-complete handler) so every widget stays in sync.
    const widgetCtx: ProjectWidgetCtx = {
      project: editing,
      assignments: projectAssignments,
      members,
      billingRoles,
      userPermissions: authUser?.permissions || [],
      onDrill: handleDrill,
      phases,
      deliverables,
      canSetPhase: canEditProject,
      onSetPhase: async (phaseName: string) => {
        await setProjectPhase(editing, phaseName);
        if (editing.id) await syncAndLoadPhases(editing.id);
      },
      // Parity with the pre-split dashboard editor: deliverable CRUD is
      // open in the UI; the server enforces the real permission.
      canEditDeliverables: !!editing.id,
      onSaveDeliverable: saveDeliverableForEditing,
      onDeleteDeliverable: deleteDeliverable,
    };

    const teamBody = !editing.id ? (
      <div
        style={{
          padding: '10px 14px',
          textAlign: 'center',
          color: 'var(--tm)',
          fontSize: 13,
          fontStyle: 'italic',
        }}
      >
        Save the project first to assign team members.
      </div>
    ) : (
      <>
        {(editing.picId || editing.pmId) &&
          (() => {
            const picMember = editing.picId ? members.find((m) => m.id === editing.picId) : null;
            const pmMember = editing.pmId ? members.find((m) => m.id === editing.pmId) : null;
            return (
              <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
                {picMember && (
                  <div
                    style={{
                      flex: 1,
                      minWidth: 200,
                      background: 'var(--sa)',
                      border: '1px solid var(--ac)',
                      borderRadius: 8,
                      padding: '10px 14px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: 'var(--ac)',
                        textTransform: 'uppercase',
                        letterSpacing: '.08em',
                      }}
                    >
                      Principal-in-Charge
                    </span>
                    <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--tx)' }}>
                      {picMember.name}
                    </span>
                    {picMember.role && (
                      <span style={{ fontSize: 11, color: 'var(--tm)' }}>{picMember.role}</span>
                    )}
                  </div>
                )}
                {pmMember && (
                  <div
                    style={{
                      flex: 1,
                      minWidth: 200,
                      background: 'var(--sa)',
                      border: '1px solid var(--ac)',
                      borderRadius: 8,
                      padding: '10px 14px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: 'var(--ac)',
                        textTransform: 'uppercase',
                        letterSpacing: '.08em',
                      }}
                    >
                      Project Manager
                    </span>
                    <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--tx)' }}>
                      {pmMember.name}
                    </span>
                    {pmMember.role && (
                      <span style={{ fontSize: 11, color: 'var(--tm)' }}>{pmMember.role}</span>
                    )}
                  </div>
                )}
              </div>
            );
          })()}
        {projectAssignments.length === 0 && !showMemberPicker && !showTeamPicker && (
          <div
            style={{
              padding: '10px 14px',
              textAlign: 'center',
              color: 'var(--tm)',
              fontSize: 13,
              fontStyle: 'italic',
            }}
          >
            No team members assigned yet.
          </div>
        )}
        {projectAssignments.length > 0 &&
          (() => {
            const byTeam = new Map<
              string,
              { team: Team; rows: { a: ProjectAssignment; m: TeamMember }[] }
            >();
            const unteamed: { a: ProjectAssignment; m: TeamMember }[] = [];
            for (const a of projectAssignments) {
              const m = members.find((mm) => mm.id === a.teamMemberId);
              if (!m) continue;
              const memberTeams = teamAssignments
                .filter((ta) => ta.teamMemberId === m.id)
                .map((ta) => teams.find((t) => t.id === ta.teamId))
                .filter((t): t is Team => !!t);
              if (memberTeams.length === 0) {
                unteamed.push({ a, m });
              } else {
                for (const t of memberTeams) {
                  const slot = byTeam.get(t.id) || { team: t, rows: [] };
                  slot.rows.push({ a, m });
                  byTeam.set(t.id, slot);
                }
              }
            }
            const groups: {
              id: string;
              title: string;
              color?: string;
              shortCode?: string;
              rows: { a: ProjectAssignment; m: TeamMember }[];
            }[] = [];
            Array.from(byTeam.values())
              .sort((x, y) => x.team.name.localeCompare(y.team.name))
              .forEach((g) =>
                groups.push({
                  id: g.team.id,
                  title: g.team.name,
                  color: g.team.color,
                  shortCode: g.team.shortCode,
                  rows: g.rows,
                })
              );
            if (unteamed.length > 0)
              groups.push({ id: '__other', title: 'Other Members', rows: unteamed });
            return (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                  gap: 10,
                  marginBottom: 8,
                  alignItems: 'start',
                }}
              >
                {groups.map((g) => (
                  <div
                    key={g.id}
                    style={{
                      background: 'var(--sa)',
                      border: '1px solid var(--bd)',
                      borderRadius: 8,
                      overflow: 'hidden',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <div
                      style={{
                        padding: '8px 12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        background: 'var(--bg)',
                        borderBottom: '1px solid var(--bd)',
                      }}
                    >
                      {g.shortCode ? (
                        <span
                          style={{
                            width: 20,
                            height: 20,
                            borderRadius: 4,
                            background: g.color || 'var(--ac)',
                            color: 'var(--color-text-inverse)',
                            fontSize: 10,
                            fontWeight: 800,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          {g.shortCode}
                        </span>
                      ) : g.color ? (
                        <span
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            background: g.color,
                            flexShrink: 0,
                          }}
                        />
                      ) : (
                        <span
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            background: 'var(--tm)',
                            opacity: 0.5,
                            flexShrink: 0,
                          }}
                        />
                      )}
                      <span
                        style={{
                          fontSize: 13,
                          fontWeight: 700,
                          flex: 1,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {g.title}
                      </span>
                      <span style={{ fontSize: 10, color: 'var(--tm)', fontWeight: 600 }}>
                        {g.rows.length}
                      </span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      {g.rows.map(({ a, m }) => {
                        const roleBadge =
                          a.role === 'pic'
                            ? {
                                text: 'PIC',
                                bg: 'color-mix(in srgb, var(--color-warning) 15%, transparent)',
                                color: 'var(--color-warning)',
                              }
                            : a.role === 'pm'
                              ? { text: 'PM', bg: 'rgba(var(--acr),0.12)', color: 'var(--ac)' }
                              : null;
                        return (
                          <div
                            key={`${g.id}-${a.id}`}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              padding: '8px 10px',
                              borderTop: g.rows[0].a.id === a.id ? 'none' : '1px solid var(--bd)',
                            }}
                          >
                            <div
                              style={{
                                width: 26,
                                height: 26,
                                borderRadius: '50%',
                                background: 'var(--ac)',
                                color: 'var(--color-text-inverse)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: 10,
                                fontWeight: 700,
                                flexShrink: 0,
                              }}
                            >
                              {m.name
                                .split(' ')
                                .map((n) => n[0])
                                .join('')
                                .toUpperCase()
                                .slice(0, 2)}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div
                                style={{
                                  fontSize: 13,
                                  fontWeight: 600,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 6,
                                }}
                              >
                                <span
                                  style={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {m.name}
                                </span>
                                {roleBadge && (
                                  <span
                                    style={{
                                      fontSize: 10,
                                      padding: '1px 5px',
                                      borderRadius: 3,
                                      background: roleBadge.bg,
                                      color: roleBadge.color,
                                      fontWeight: 700,
                                      flexShrink: 0,
                                      letterSpacing: '.03em',
                                    }}
                                  >
                                    {roleBadge.text}
                                  </span>
                                )}
                              </div>
                              {/* Affinity pills: department (always when known)
                                  + team (when this row's group is "Other" so
                                  the team isn't already implicit from the
                                  card header) + a forward-looking
                                  "next 4 weeks" forecast pill so a PM
                                  immediately sees who's been assigned but
                                  has nothing planned coming up. Each pill
                                  carries its own color. */}
                              {(() => {
                                const dept = m.departmentId
                                  ? departments.find((d) => d.id === m.departmentId)
                                  : null;
                                const teamPill =
                                  g.id === '__other'
                                    ? null
                                    : g.color
                                      ? {
                                          label: g.title,
                                          color: g.color,
                                          kind: 'team' as const,
                                          warn: false,
                                        }
                                      : null;
                                const deptPill = dept
                                  ? {
                                      label: dept.abbreviation || dept.name,
                                      color: dept.color || 'var(--ac)',
                                      kind: 'department' as const,
                                      warn: false,
                                    }
                                  : null;
                                // Sum non-vacation forecast hours for this
                                // (member, project) over the next-4-weeks window
                                // already loaded in `upcomingForecasts`.
                                let upcomingHours = 0;
                                for (const f of upcomingForecasts) {
                                  if (f.sourceKind === 'vacation') continue;
                                  if (f.teamMemberId !== m.id) continue;
                                  if (f.projectId !== a.projectId) continue;
                                  upcomingHours += Number(f.hours) || 0;
                                }
                                const forecastPill =
                                  upcomingHours > 0
                                    ? {
                                        label: `${upcomingHours}h next 4 wks`,
                                        color: 'var(--color-success)',
                                        kind: 'forecast' as const,
                                        warn: false,
                                      }
                                    : {
                                        label: 'No forecast',
                                        color: 'var(--color-warning)',
                                        kind: 'forecast' as const,
                                        warn: true,
                                      };
                                const pills = [deptPill, teamPill, forecastPill].filter(
                                  (p): p is NonNullable<typeof p> => !!p
                                );
                                if (pills.length === 0) return null;
                                return (
                                  <div
                                    style={{
                                      display: 'flex',
                                      gap: 3,
                                      flexWrap: 'wrap',
                                      marginTop: 2,
                                    }}
                                  >
                                    {pills.map((p) => (
                                      <span
                                        key={`${p.kind}:${p.label}`}
                                        title={
                                          p.kind === 'department'
                                            ? `Department: ${p.label}`
                                            : p.kind === 'team'
                                              ? `Team: ${p.label}`
                                              : upcomingHours > 0
                                                ? `${upcomingHours}h forecasted on this project for the next 4 weeks`
                                                : 'Assigned to this project but no hours forecasted in the next 4 weeks. Plan some on the Forecasting page.'
                                        }
                                        // Forecast status renders as quiet colored TEXT, not a
                                        // boxed chip — six amber boxes in a card reads as an
                                        // alarm wall; the color + icon + tooltip carry the signal.
                                        style={{
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: 3,
                                          fontSize: 10,
                                          padding: p.kind === 'forecast' ? '1px 0' : '1px 5px',
                                          borderRadius: 3,
                                          fontWeight: p.kind === 'forecast' ? 600 : 700,
                                          letterSpacing: '.03em',
                                          background:
                                            p.kind === 'forecast' ? 'transparent' : p.color + '22',
                                          color: p.color,
                                          border:
                                            p.kind === 'forecast'
                                              ? 'none'
                                              : `1px solid ${p.color}40`,
                                        }}
                                      >
                                        {p.warn && <AlertTriangle size={9} aria-hidden="true" />}
                                        {p.label}
                                      </span>
                                    ))}
                                  </div>
                                );
                              })()}
                            </div>
                            <div style={{ width: 92, flexShrink: 0 }}>
                              <SearchableSelect
                                value={a.role || 'member'}
                                options={[
                                  { value: 'pm', label: 'PM' },
                                  { value: 'pic', label: 'PIC' },
                                  { value: 'member', label: 'Member' },
                                ]}
                                onChange={(v) => changeAssignmentRole(a.id, v || 'member')}
                                clearable={false}
                                style={{ padding: '3px 8px', fontSize: 11, minHeight: 26 }}
                              />
                            </div>
                            <button
                              onClick={() => unassignMemberFromProject(a.id)}
                              style={{
                                background: 'transparent',
                                border: 'none',
                                color: 'var(--dg)',
                                cursor: 'pointer',
                                padding: 8,
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                              }}
                              title="Remove from project"
                              aria-label="Remove from project"
                            >
                              <X size={13} aria-hidden="true" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
        {showMemberPicker && (
          <div
            style={{
              marginBottom: 8,
              padding: 10,
              background: 'var(--sa)',
              borderRadius: 8,
              border: '1px solid var(--bd)',
            }}
          >
            <MemberPickerInline
              availableMembers={availableMembersForProject}
              onSelect={async (id) => {
                await assignMemberToProject(id);
              }}
            />
          </div>
        )}
        {showTeamPicker && teams.length > 0 && (
          <div
            style={{
              marginBottom: 8,
              padding: 10,
              background: 'var(--sa)',
              borderRadius: 8,
              border: '1px solid var(--bd)',
            }}
          >
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--tm)', marginBottom: 6 }}>
              Pick a team — every member not already on the project gets assigned:
            </div>
            <SearchableSelect
              value=""
              onChange={(v) => {
                if (v) assignTeamToProject(v);
              }}
              placeholder="Search teams…"
              searchPlaceholder="Search teams…"
              clearable={false}
              options={teams.map((t) => {
                const memberIds = teamAssignments
                  .filter((ta) => ta.teamId === t.id)
                  .map((ta) => ta.teamMemberId);
                const assignedIds = new Set(projectAssignments.map((a) => a.teamMemberId));
                const newCount = memberIds.filter((id) => !assignedIds.has(id)).length;
                const totalCount = memberIds.length;
                const allOnProject = totalCount > 0 && newCount === 0;
                return {
                  value: t.id,
                  label: t.name + (t.shortCode ? `  (${t.shortCode})` : ''),
                  hint: allOnProject
                    ? 'all assigned'
                    : totalCount === 0
                      ? 'no members'
                      : `+${newCount} new · ${totalCount} total`,
                };
              })}
            />
          </div>
        )}
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => {
              setShowMemberPicker(!showMemberPicker);
              setShowTeamPicker(false);
            }}
            style={{
              padding: '5px 12px',
              borderRadius: 5,
              border: '1px solid var(--bd)',
              background: showMemberPicker ? 'var(--sa)' : 'transparent',
              color: 'var(--ac)',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {showMemberPicker ? 'Close' : '+ Assign Member'}
          </button>
          {teams.length > 0 && (
            <button
              onClick={() => {
                setShowTeamPicker(!showTeamPicker);
                setShowMemberPicker(false);
              }}
              style={{
                padding: '5px 12px',
                borderRadius: 5,
                border: '1px solid var(--bd)',
                background: showTeamPicker ? 'var(--sa)' : 'transparent',
                color: 'var(--ac)',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {showTeamPicker ? 'Close' : '+ Assign Team'}
            </button>
          )}
        </div>

        {/* Per-member utilization strip moved out into the standalone
         *  Team Utilization widget in v2.11.7 — the Project Team widget
         *  now focuses on people management (PIC/PM, assigning members
         *  and teams). Open the Team Utilization widget for capacity. */}
      </>
    );

    // Compact team — just initials bubbles in a flex wrap. Shows at 1-col width.
    const teamCompactBody = !editing.id ? (
      teamBody
    ) : (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {projectAssignments.map((a) => {
          const m = members.find((mm) => mm.id === a.teamMemberId);
          if (!m) return null;
          const initials = m.name
            .split(' ')
            .map((n) => n[0])
            .join('')
            .toUpperCase()
            .slice(0, 2);
          const isPic = a.role === 'pic';
          const isPm = a.role === 'pm';
          return (
            <div
              key={a.id}
              title={`${m.name}${isPic ? ' (PIC)' : isPm ? ' (PM)' : ''}`}
              style={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                background: isPic || isPm ? 'var(--ac)' : 'var(--sa)',
                color: isPic || isPm ? 'var(--color-text-inverse)' : 'var(--tx)',
                border: isPic
                  ? '2px solid var(--color-warning)'
                  : isPm
                    ? '2px solid var(--ac)'
                    : '1px solid var(--bd)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 11,
                fontWeight: 700,
                cursor: 'default',
              }}
            >
              {initials}
            </div>
          );
        })}
        {projectAssignments.length === 0 && (
          <div style={{ fontSize: 13, color: 'var(--tm)', fontStyle: 'italic' }}>
            No team assigned
          </div>
        )}
      </div>
    );

    // widgetCtx hoisted earlier in the closure to keep the team-body
    // capacity strip happy — see comment up where teamBody is built.

    // Widget definitions — closures capture the rendered bodies. WidgetGrid
    // handles their titles, customize UI, layout persistence, permission gating.
    const projectWidgets: WidgetDef[] = [
      {
        id: 'details',
        title: 'Project Details',
        description: 'Project number, client, type, PM/PIC, budget, and scopes.',
        defaultSize: 'standard',
        category: 'overview',
        // Expanded would only stretch the same fields — not offered.
        sizes: ['compact', 'standard'],
        rows: { compact: 4, standard: 8 },
        render: (_ctx, size: WidgetSize) => (size === 'compact' ? detailsCompactBody : detailsBody),
      },
      {
        id: 'team',
        title: 'Project Team',
        description: 'PIC, PM, and team-member assignments.',
        defaultSize: 'expanded',
        category: 'people',
        // Compact = avatar chips; expanded = the full management grid.
        // Standard rendered the identical grid squeezed — not offered.
        sizes: ['compact', 'expanded'],
        render: (_ctx, size: WidgetSize) => (size === 'compact' ? teamCompactBody : teamBody),
      },
      // ─── Financial widgets ───
      {
        id: 'budget_hours',
        title: 'Hours: Budget vs Actual',
        description:
          'Project hours actuals vs budget. Expanded view shows phase breakdown, weekly burn, projected exhaustion.',
        defaultSize: 'compact',
        category: 'financial',
        render: (_, size) => <BudgetVsActualHoursWidget ctx={widgetCtx} size={size as any} />,
      },
      {
        id: 'budget_dollars',
        title: 'Cost: Budget vs Actual',
        description:
          'Actual cost (hours × rate) vs dollar budget. Falls back to hours-only when financials are hidden.',
        defaultSize: 'compact',
        category: 'financial',
        render: (_, size) => <BudgetVsActualDollarsWidget ctx={widgetCtx} size={size as any} />,
      },
      {
        id: 'profitability',
        title: 'Profitability',
        description: 'Revenue (invoiced) − cost (hours × rate). Margin % and contract projection.',
        defaultSize: 'standard',
        category: 'financial',
        requires: ['projects.view_financials'],
        rows: { standard: 3 },
        render: (_, size) => <ProfitabilityWidget ctx={widgetCtx} size={size as any} />,
      },
      {
        id: 'ar_aging',
        title: 'AR Aging',
        description:
          'Outstanding receivables bucketed by age. Expanded view lists invoices with click-through.',
        defaultSize: 'standard',
        category: 'financial',
        rows: { standard: 3 },
        render: (_, size) => <ARagingWidget ctx={widgetCtx} size={size as any} />,
      },
      // ─── Schedule / health ───
      {
        id: 'phases',
        title: 'Project Phase',
        description:
          'Phase stepper — timekeeping and forecasting context. Click to advance the current phase.',
        defaultSize: 'standard',
        category: 'schedule',
        // Expanded would only stretch the stepper — not offered.
        sizes: ['compact', 'standard'],
        rows: { standard: 6 },
        render: (_, size) => <PhasesWidget ctx={widgetCtx} size={size as any} />,
      },
      {
        id: 'deliverables',
        title: 'Deliverables',
        description:
          'Standalone deliverables (name + due date). Compact list by default; Gantt timeline + full editor when expanded.',
        defaultSize: 'standard',
        category: 'schedule',
        rows: { standard: 5 },
        render: (_, size) => <DeliverablesWidget ctx={widgetCtx} size={size as any} />,
      },
      {
        id: 'phase_health',
        title: 'Current Phase Health',
        description: 'Active phase actuals vs phase budget — hours, cost, and time elapsed.',
        defaultSize: 'standard',
        category: 'health',
        rows: { standard: 3 },
        render: (_, size) => <PhaseHealthWidget ctx={widgetCtx} size={size as any} />,
      },
      {
        id: 'risks',
        title: 'Risks / Open Items',
        description: 'Open project tasks ranked by priority. Click to view, + adds new.',
        defaultSize: 'standard',
        category: 'health',
        rows: { standard: 5 },
        render: (_, size) => <RisksOpenItemsWidget ctx={widgetCtx} size={size as any} />,
      },
      // ─── Forecasting / utilization ───
      {
        id: 'forecast_outlook',
        title: 'Forecast — Next 4 Weeks',
        description:
          'Hours forecasted on this project for the upcoming 4 weeks. Per-person in expanded.',
        defaultSize: 'standard',
        category: 'people',
        render: (_, size) => <ForecastOutlookWidget ctx={widgetCtx} size={size as any} />,
      },
      {
        id: 'burn_rate',
        title: 'Burn Rate — Last 8 Weeks',
        description:
          'Weekly hours trend with forecast overlay and budget pace reference in expanded view.',
        defaultSize: 'standard',
        category: 'financial',
        render: (_, size) => <BurnRateWidget ctx={widgetCtx} size={size as any} />,
      },
      {
        id: 'team_utilization',
        title: 'Team Utilization',
        description:
          'Per-person hours this week + 4-week trajectory sparkbars vs capacity. Click for member.',
        defaultSize: 'standard',
        category: 'people',
        render: (_, size) => <TeamUtilizationWidget ctx={widgetCtx} size={size as any} />,
      },
      {
        id: 'variance',
        title: 'Forecast vs Actual',
        description:
          'Rolling 4-week variance: how accurate were our forecasts? Per-week breakdown in expanded.',
        defaultSize: 'standard',
        category: 'health',
        render: (_, size) => <VarianceWidget ctx={widgetCtx} size={size as any} />,
      },
      {
        id: 'recent_activity',
        title: 'Recent Activity',
        description:
          'Time entries, invoice events, and deliverable status changes from the last 30 days.',
        defaultSize: 'standard',
        category: 'overview',
        // A feed has no honest one-number compact form — not offered.
        sizes: ['standard', 'expanded'],
        rows: { standard: 5 },
        render: (_, size) => <RecentActivityWidget ctx={widgetCtx} size={size as any} />,
      },
    ];

    // Default layout: hero overview row, schedule + financial tiles,
    // team + utilization, then recent activity. Honest information
    // density at every size — pickers route to a real "expanded" view.
    const DEFAULT_PROJECT_DASHBOARD: WidgetLayoutEntry[] = [
      { id: 'details', size: 'standard' },
      { id: 'budget_hours', size: 'compact' },
      { id: 'budget_dollars', size: 'compact' },
      { id: 'profitability', size: 'compact' },
      { id: 'ar_aging', size: 'compact' },
      { id: 'phases', size: 'standard' },
      { id: 'deliverables', size: 'standard' },
      { id: 'team', size: 'expanded' },
      { id: 'team_utilization', size: 'standard' },
      { id: 'forecast_outlook', size: 'standard' },
      { id: 'burn_rate', size: 'standard' },
      { id: 'variance', size: 'standard' },
      { id: 'risks', size: 'standard' },
      { id: 'recent_activity', size: 'standard' },
    ];

    return (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Header bar */}
        <div
          style={{
            padding: '12px 24px',
            borderBottom: '1px solid var(--bd)',
            background: 'var(--sf)',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flexShrink: 0,
          }}
        >
          <button
            onClick={closeDetail}
            style={{
              padding: '5px 10px',
              borderRadius: 5,
              border: '1px solid var(--bd)',
              background: 'transparent',
              color: 'var(--tm)',
              cursor: 'pointer',
              fontSize: 13,
              display: 'flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            <ChevronLeft size={14} aria-hidden="true" /> Projects
          </button>
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 10 }}>
            {editing.projectNumber && (
              <span
                style={{
                  fontSize: 14,
                  fontFamily: "'JetBrains Mono', monospace",
                  color: 'var(--ac)',
                  fontWeight: 700,
                }}
              >
                {editing.projectNumber}
              </span>
            )}
            <span style={{ fontSize: 18, fontWeight: 700 }}>
              {isNew ? 'New Project' : sanitizeProjectName(editing.name) || 'Untitled'}
            </span>
            {!isNew && (
              <span
                style={{
                  fontSize: 10,
                  padding: '2px 8px',
                  borderRadius: 3,
                  background: statusBadge(editing.status || 'active').bg,
                  color: statusBadge(editing.status || 'active').color,
                  fontWeight: 600,
                }}
              >
                {statusBadge(editing.status || 'active').text}
              </span>
            )}
            {/* Edit button moved to the Details widget */}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {projectDetailTab === 'dashboard' && (
              <>
                <button
                  onClick={exportDashboardPdf}
                  title="Print or export this dashboard view as PDF"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '6px 12px',
                    borderRadius: 6,
                    border: '1px solid var(--bd)',
                    background: 'transparent',
                    color: 'var(--tm)',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  <Download size={13} aria-hidden="true" /> Export
                </button>
                <button
                  onClick={() => setCustomizingDashboard((c) => !c)}
                  title="Rearrange, resize, hide, or add widgets"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '6px 12px',
                    borderRadius: 6,
                    border: `1px solid ${customizingDashboard ? 'var(--ac)' : 'var(--bd)'}`,
                    background: customizingDashboard ? 'rgba(var(--acr),0.08)' : 'transparent',
                    color: customizingDashboard ? 'var(--ac)' : 'var(--tm)',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  {customizingDashboard ? (
                    <>
                      <Check size={13} aria-hidden="true" /> Done customizing
                    </>
                  ) : (
                    <>
                      <Settings size={13} aria-hidden="true" /> Customize
                    </>
                  )}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Tab bar — Dashboard / Specs */}
        {!isNew && (
          <div
            style={{
              display: 'flex',
              gap: 0,
              padding: '0 24px',
              borderBottom: '1px solid var(--bd)',
              background: 'var(--sf)',
              flexShrink: 0,
            }}
          >
            {(['dashboard', 'specs', 'formas'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setProjectDetailTab(tab)}
                style={{
                  padding: '8px 20px',
                  border: 'none',
                  borderBottom:
                    projectDetailTab === tab ? '2px solid var(--ac)' : '2px solid transparent',
                  background: 'transparent',
                  color: projectDetailTab === tab ? 'var(--ac)' : 'var(--tm)',
                  fontSize: 13,
                  fontWeight: projectDetailTab === tab ? 700 : 500,
                  cursor: 'pointer',
                  transition: 'all .15s',
                }}
              >
                {tab === 'dashboard'
                  ? 'Dashboard'
                  : tab === 'specs'
                    ? `Specs (${projectSpecs.length})`
                    : 'Forma'}
              </button>
            ))}
          </div>
        )}

        {/* Dashboard tab — widget grid */}
        {projectDetailTab === 'dashboard' && (
          <div id="project-dashboard-grid" style={{ flex: 1, overflow: 'auto', padding: 24 }}>
            <WidgetGrid
              widgets={projectWidgets}
              layout={migrateDashboardLayout(dashboardLayout)}
              defaultLayout={DEFAULT_PROJECT_DASHBOARD}
              userPermissions={authUser?.permissions}
              ctx={{}}
              customizing={customizingDashboard}
              onLayoutChange={saveDashboardLayout}
              viewsBar={
                <DashboardViewsBar
                  views={dashboardViews}
                  activeViewId={activeViewId}
                  currentUserId={authUser?.teamMemberId || authUser?.id || ''}
                  onSwitch={switchToView}
                  onSaveAs={() => setShowSaveViewDialog(true)}
                  onSetDefault={setActiveAsDefault}
                  onDelete={deleteActiveView}
                />
              }
            />
            {showSaveViewDialog && (
              <div
                onMouseDown={(e) => {
                  if (e.target === e.currentTarget) setShowSaveViewDialog(false);
                }}
                style={{
                  position: 'fixed',
                  inset: 0,
                  background: 'rgba(0,0,0,.4)',
                  zIndex: 950,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <div
                  onMouseDown={(e) => e.stopPropagation()}
                  style={{
                    width: 380,
                    background: 'var(--sf)',
                    borderRadius: 10,
                    padding: 20,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 12,
                    boxShadow: '0 12px 40px rgba(0,0,0,.3)',
                  }}
                >
                  <div style={{ fontSize: 16, fontWeight: 700 }}>Save current layout as a view</div>
                  <input
                    autoFocus
                    type="text"
                    placeholder="View name (e.g. PIC view, Accounting)"
                    value={saveViewName}
                    onChange={(e) => setSaveViewName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') saveAsNewView();
                    }}
                    style={{
                      padding: '8px 12px',
                      borderRadius: 6,
                      border: '1px solid var(--bd)',
                      background: 'var(--ib)',
                      color: 'var(--tx)',
                      fontSize: 13,
                    }}
                  />
                  <label
                    style={{
                      display: 'flex',
                      gap: 8,
                      alignItems: 'center',
                      fontSize: 13,
                      color: 'var(--tm)',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={saveViewShared}
                      onChange={(e) => setSaveViewShared(e.target.checked)}
                    />
                    Share with the firm (everyone sees this view)
                  </label>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowSaveViewDialog(false)}
                    >
                      Cancel
                    </Button>
                    <Button size="sm" onClick={saveAsNewView} disabled={!saveViewName.trim()}>
                      Save view
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Specs tab — full DataTable spec management, mirrors SpecLibrary */}
        {projectDetailTab === 'specs' && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Specs toolbar */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '10px 20px',
                borderBottom: '1px solid var(--bd)',
                background: 'var(--sf)',
                flexWrap: 'wrap',
              }}
            >
              <button
                onClick={() => navigate?.({ view: 'compile', projectId: editing!.id })}
                title="Add master sections to this project"
                style={{
                  padding: '7px 16px',
                  borderRadius: 6,
                  border: 'none',
                  background: 'var(--ac)',
                  color: 'var(--color-text-inverse)',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                }}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                >
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Add Specs
              </button>
              <button
                onClick={() => void handleImportDocxToProject()}
                disabled={importingDocx}
                title="Import existing .docx specs straight into this project (bypasses the firm masters)"
                style={{
                  padding: '7px 14px',
                  borderRadius: 6,
                  border: '1px solid var(--bd)',
                  background: 'var(--sf)',
                  color: 'var(--tx)',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: importingDocx ? 'default' : 'pointer',
                  opacity: importingDocx ? 0.6 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                }}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
                {importingDocx ? 'Importing…' : 'Import .docx'}
              </button>
              {projectSpecs.length > 0 && (
                <button
                  onClick={() => {
                    // Preselect: the project's OWN forma copy (specs-style —
                    // applying a master placed a copy in the project) → a
                    // master attached to this project → most-used among the
                    // specs' bindings → NOTHING (explicit pick; an arbitrary
                    // first-forma guess is worse than empty).
                    const projectCopy = formas.find(
                      (f: any) => f.projectId === editing?.id && !f.specId
                    );
                    const attached = formas.find(
                      (f: any) => f.attachedProjectId === editing?.id && !f.projectId
                    );
                    const counts = new Map<string, number>();
                    for (const s of projectSpecs)
                      if (s.formaId) counts.set(s.formaId, (counts.get(s.formaId) || 0) + 1);
                    const top =
                      projectCopy?.id ||
                      attached?.id ||
                      [...counts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ||
                      '';
                    setExportFormaId(top);
                    setShowExportModal(true);
                  }}
                  style={{
                    padding: '7px 16px',
                    borderRadius: 6,
                    border: '1px solid var(--ac)',
                    background: 'transparent',
                    color: 'var(--ac)',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 5,
                  }}
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                  Export
                </button>
              )}
              <span style={{ fontSize: 13, color: 'var(--tm)' }}>
                {projectSpecs.length} spec{projectSpecs.length !== 1 ? 's' : ''}
              </span>
            </div>
            {/* Specs DataTable */}
            <div style={{ flex: 1, overflow: 'auto', padding: 20 }}>
              {projectSpecs.length === 0 ? (
                <EmptyState
                  title="No specs in this project yet."
                  subtitle="Pull master sections in to start building the project manual."
                  action={{
                    label: 'Add Specs',
                    onClick: () => navigate?.({ view: 'compile', projectId: editing!.id }),
                  }}
                />
              ) : (
                (() => {
                  const divOf = (csi: string) => {
                    const m = (csi || '').trim().match(/^(\d{2})/);
                    return m ? m[1] : 'Other';
                  };
                  const latestIssuance = (s: ProjectSpec) =>
                    realIssuances(s.issuances).slice(-1)[0]?.name || '';
                  const specColumns: Column<ProjectSpec>[] = [
                    {
                      key: 'csiNumber',
                      label: 'CSI #',
                      width: 100,
                      sortable: true,
                      getValue: (s) => s.csiNumber || '',
                    },
                    {
                      key: 'title',
                      label: 'Title',
                      sortable: true,
                      getValue: (s) => s.title || '',
                    },
                    {
                      key: 'issuance',
                      label: 'Latest Issuance',
                      width: 140,
                      sortable: true,
                      getValue: (s) => latestIssuance(s),
                      render: (s) => {
                        const name = latestIssuance(s);
                        if (!name)
                          return <span style={{ color: 'var(--tm)', fontStyle: 'italic' }}>—</span>;
                        return (
                          <span
                            style={{
                              padding: '2px 8px',
                              borderRadius: 999,
                              background: 'rgba(var(--acr),0.12)',
                              color: 'var(--ac)',
                              fontSize: 10,
                              fontWeight: 600,
                              letterSpacing: '.02em',
                            }}
                          >
                            {name}
                          </span>
                        );
                      },
                    },
                    {
                      key: 'editedBy',
                      label: 'Last Edited By',
                      width: 140,
                      sortable: true,
                      getValue: (s) => s.editedBy || '',
                    },
                    {
                      key: 'lastEdited',
                      label: 'Last Edited',
                      width: 140,
                      sortable: true,
                      getValue: (s) =>
                        s.lastEdited ? new Date(s.lastEdited).toLocaleDateString() : '',
                    },
                  ];
                  const specGroupOptions: GroupOption<ProjectSpec>[] = [
                    {
                      key: 'division',
                      label: 'By Division',
                      getValue: (s) => {
                        const d = divOf(s.csiNumber || '');
                        return `Division ${d}`;
                      },
                    },
                  ];
                  return (
                    <DataTable<ProjectSpec>
                      columns={specColumns}
                      data={projectSpecs}
                      keyField="id"
                      onRowClick={(s) => onOpenProjectSpec?.(s.id, editing!.id)}
                      groupOptions={specGroupOptions}
                      searchPlaceholder="Search project specs..."
                      initialSort={{ key: 'csiNumber', direction: 'asc' }}
                      storageKey={`project-specs-${editing!.id}`}
                      actions={(s) => (
                        <div style={{ display: 'flex', gap: 4 }}>
                          {canDeleteSpecs && (
                            <button
                              onClick={async (e) => {
                                e.stopPropagation();
                                if (
                                  !confirm(
                                    `Remove "${s.csiNumber} — ${s.title}" from this project?`
                                  )
                                )
                                  return;
                                try {
                                  await ProjectSpecService.delete(s.id);
                                  setProjectSpecs((prev) => prev.filter((x) => x.id !== s.id));
                                  toast('Spec removed');
                                } catch (err: any) {
                                  toast('Failed: ' + (err.message || ''), true);
                                }
                              }}
                              title="Remove from project"
                              aria-label="Remove from project"
                              style={{
                                background: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                color: 'var(--dg)',
                                padding: 8,
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <Trash2 size={14} aria-hidden="true" />
                            </button>
                          )}
                        </div>
                      )}
                    />
                  );
                })()
              )}
            </div>
          </div>
        )}
        {/* Formas tab — the project's ONE forma (master fork, LP-3 Brick 1).
            Placing a master forks a project copy; the copy rules every export
            in this project. Style edits open the designer on the copy. */}
        {projectDetailTab === 'formas' && (
          <div style={{ flex: 1, overflow: 'auto', padding: 'var(--space-5)' }}>
            {(() => {
              if (!editing.id) {
                return (
                  <EmptyState
                    title="Save the project first"
                    subtitle="A forma can be placed once the project exists."
                  />
                );
              }
              const projectFormas = formas.filter((f) => f.projectId === editing.id && !f.specId);
              const masters = formas.filter((f) => !f.projectId && !f.specId);
              const masterOptions = masters.map((m) => ({
                value: m.id,
                label: m.docSettings?.standardName || 'Untitled forma',
              }));
              const masterNameOf = (f: StandardConfig) =>
                masters.find((m) => m.id === f.sourceFormaId)?.docSettings?.standardName;
              const lastEdit = (f: StandardConfig) => {
                const h = f.editHistory?.[f.editHistory.length - 1];
                return h
                  ? `${h.action === 'created' ? 'Created' : 'Edited'} by ${h.userName}${h.timestamp ? ' · ' + new Date(h.timestamp).toLocaleDateString() : ''}`
                  : null;
              };

              const picker = (buttonLabel: string) => (
                <div
                  style={{
                    display: 'flex',
                    gap: 'var(--space-2)',
                    alignItems: 'center',
                    maxWidth: 460,
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <SearchableSelect
                      value={formaPickerId}
                      onChange={(v) => setFormaPickerId(v || '')}
                      options={masterOptions}
                      placeholder="Pick a master forma…"
                      searchPlaceholder="Search master formas…"
                    />
                  </div>
                  <Button
                    onClick={() => placeFormaFromMaster(formaPickerId)}
                    disabled={!formaPickerId || placingForma}
                  >
                    {placingForma ? 'Placing…' : buttonLabel}
                  </Button>
                </div>
              );

              if (projectFormas.length === 0) {
                return (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 'var(--space-4)',
                      paddingTop: 'var(--space-6)',
                    }}
                  >
                    <EmptyState
                      title="No project forma yet"
                      subtitle="Place a copy of a master forma in this project. The copy is yours to tune — the master stays pristine — and every spec export in this project uses it."
                    />
                    {masters.length === 0 ? (
                      <div style={{ fontSize: 'var(--font-size-base)', color: 'var(--tm)' }}>
                        No master formas exist yet — create one in Master Formas first.
                      </div>
                    ) : (
                      picker('Place in project')
                    )}
                  </div>
                );
              }

              return (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--space-4)',
                    maxWidth: 720,
                  }}
                >
                  {projectFormas.length > 1 && (
                    <div
                      style={{
                        fontSize: 'var(--font-size-sm)',
                        color: 'var(--color-warning)',
                        padding: 'var(--space-2) var(--space-3)',
                        background: 'color-mix(in srgb, var(--color-warning) 8%, transparent)',
                        borderRadius: 'var(--radius-md)',
                      }}
                    >
                      A project has exactly one forma — remove the extras below.
                    </div>
                  )}
                  {projectFormas.map((f) => (
                    <div
                      key={f.id}
                      style={{
                        background: 'var(--sf)',
                        border: '1px solid var(--bd)',
                        borderRadius: 'var(--radius-lg)',
                        padding: 'var(--space-4) var(--space-5)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 'var(--space-3)',
                      }}
                    >
                      <div>
                        <div
                          style={{
                            fontSize: 'var(--font-size-lg)',
                            fontWeight: 700,
                            color: 'var(--tx)',
                          }}
                        >
                          {f.docSettings?.standardName || 'Untitled forma'}
                        </div>
                        <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--tm)' }}>
                          {masterNameOf(f) ? `Forked from ${masterNameOf(f)}` : 'Project copy'}
                          {lastEdit(f) ? ` · ${lastEdit(f)}` : ''}
                        </div>
                      </div>
                      <div style={{ fontSize: 'var(--font-size-base)', color: 'var(--tm)' }}>
                        Every spec export in this project formats through this forma. Edits here
                        never touch the master.
                      </div>
                      <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                        <Button onClick={() => onOpenForma?.(f.id)} disabled={!onOpenForma}>
                          Open in Designer
                        </Button>
                        <Button variant="outline" onClick={() => removeProjectForma(f)}>
                          Remove from project
                        </Button>
                      </div>
                    </div>
                  ))}
                  {projectFormas.length === 1 && (
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 'var(--space-2)',
                        paddingTop: 'var(--space-2)',
                        borderTop: '1px solid var(--bd)',
                      }}
                    >
                      <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--tm)' }}>
                        Swap to a different master — removes the current copy (its tweaks are lost)
                        and places a fresh fork.
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          gap: 'var(--space-2)',
                          alignItems: 'center',
                          maxWidth: 460,
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <SearchableSelect
                            value={formaPickerId}
                            onChange={(v) => setFormaPickerId(v || '')}
                            options={masterOptions.filter(
                              (o) => o.value !== projectFormas[0].sourceFormaId
                            )}
                            placeholder="Pick a different master…"
                            searchPlaceholder="Search master formas…"
                          />
                        </div>
                        <Button
                          variant="outline"
                          disabled={!formaPickerId || placingForma}
                          onClick={async () => {
                            const cur = projectFormas[0];
                            if (
                              !confirm(
                                `Replace "${cur.docSettings?.standardName || 'the current forma'}"?\n\nIts project-specific tweaks are deleted and a fresh copy of the selected master takes over.`
                              )
                            )
                              return;
                            await FormaService.delete(cur.id);
                            await placeFormaFromMaster(formaPickerId);
                          }}
                        >
                          {placingForma ? 'Swapping…' : 'Swap'}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}
        {/* Export Project Specs Modal — one pipeline for docx / pdf / both with forma */}
        {showExportModal && editing && (
          <div
            onMouseDown={(e) => {
              if (e.target === e.currentTarget && !exporting) setShowExportModal(false);
            }}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,.45)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
            }}
          >
            <div
              style={{
                background: 'var(--sf)',
                borderRadius: 10,
                border: '1px solid var(--bd)',
                padding: 24,
                width: 520,
                maxHeight: '85vh',
                overflow: 'auto',
                boxShadow: '0 8px 32px rgba(0,0,0,.2)',
              }}
            >
              <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>
                Export Project Specs
              </div>
              <div style={{ fontSize: 13, color: 'var(--tm)', marginBottom: 12 }}>
                {`${exportSelIds.size} of ${projectSpecs.length} spec${
                  projectSpecs.length === 1 ? '' : 's'
                } selected · ${editing.name}`}
              </div>
              {/* Spec selection — pick any number, with select / deselect all */}
              <div style={{ marginBottom: 16, border: '1px solid var(--bd)', borderRadius: 8 }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '8px 12px',
                    borderBottom: '1px solid var(--bd)',
                  }}
                >
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '.4px',
                      color: 'var(--tm)',
                    }}
                  >
                    Specs to export
                  </span>
                  <button
                    onClick={() => {
                      const all = projectSpecs.map((s) => s.id);
                      setExportSelIds(exportSelIds.size === all.length ? new Set() : new Set(all));
                    }}
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: 'var(--ac)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    {exportSelIds.size === projectSpecs.length ? 'Deselect all' : 'Select all'}
                  </button>
                </div>
                <div style={{ maxHeight: 200, overflow: 'auto' }}>
                  {projectSpecs
                    .slice()
                    .sort((a, b) => (a.csiNumber || '').localeCompare(b.csiNumber || ''))
                    .map((s) => {
                      const on = exportSelIds.has(s.id);
                      return (
                        <div
                          key={s.id}
                          onClick={() =>
                            setExportSelIds((prev) => {
                              const n = new Set(prev);
                              if (n.has(s.id)) n.delete(s.id);
                              else n.add(s.id);
                              return n;
                            })
                          }
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            padding: '6px 12px',
                            cursor: 'pointer',
                          }}
                        >
                          <span
                            style={{
                              width: 16,
                              height: 16,
                              borderRadius: 4,
                              flexShrink: 0,
                              border: '2px solid ' + (on ? 'var(--ac)' : 'var(--bd)'),
                              background: on ? 'var(--ac)' : 'transparent',
                              color: 'var(--color-text-inverse)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            {on ? <Check size={11} aria-hidden="true" /> : null}
                          </span>
                          <span
                            style={{
                              fontFamily: "'JetBrains Mono', monospace",
                              fontSize: 11,
                              color: 'var(--ac)',
                              fontWeight: 700,
                              minWidth: 64,
                            }}
                          >
                            {s.csiNumber || '—'}
                          </span>
                          <span
                            style={{
                              flex: 1,
                              minWidth: 0,
                              fontSize: 13,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {s.title || 'Untitled'}
                          </span>
                        </div>
                      );
                    })}
                </div>
              </div>

              <div style={{ marginBottom: 14 }}>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: 'var(--tm)',
                    marginBottom: 5,
                    textTransform: 'uppercase',
                    letterSpacing: '.04em',
                  }}
                >
                  Forma
                </div>
                <SearchableSelect
                  value={exportFormaId}
                  onChange={(v) => setExportFormaId(v || '')}
                  options={formas
                    // Masters + this project's shared copy. Spec-level copies are
                    // applied automatically per spec, so they don't belong here.
                    .filter((f: any) => !f.projectId || (f.projectId === editing?.id && !f.specId))
                    .map((f: any) => ({
                      value: f.id,
                      label:
                        (f.docSettings?.standardName || 'Untitled') +
                        (f.projectId ? '  · this project' : ''),
                    }))}
                  placeholder="No forma (plain .docx)"
                  clearable={true}
                />
                <div style={{ fontSize: 10, color: 'var(--tm)', marginTop: 4 }}>
                  Formatting template applied to every exported spec. Leave blank to skip
                  formatting.
                </div>
              </div>

              <div style={{ marginBottom: 14 }}>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: 'var(--tm)',
                    marginBottom: 5,
                    textTransform: 'uppercase',
                    letterSpacing: '.04em',
                  }}
                >
                  Deliverable
                </div>
                <SearchableSelect
                  value={exportDeliverable}
                  onChange={(v) => setExportDeliverable(v || '')}
                  options={exportDeliverables.map((d) => ({ value: d.name, label: d.name }))}
                  placeholder="No deliverable stamp"
                  clearable={true}
                />
                <div style={{ fontSize: 10, color: 'var(--tm)', marginTop: 4 }}>
                  Fills the {'{Deliverable}'} token in the forma — e.g. headers, footers, cover.
                  Leave blank if this export isn’t tied to a deliverable.
                </div>
              </div>

              <div style={{ marginBottom: 14 }}>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: 'var(--tm)',
                    marginBottom: 5,
                    textTransform: 'uppercase',
                    letterSpacing: '.04em',
                  }}
                >
                  Format
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {/* Web: PDF conversion is desktop-only (Word COM) — docx only. */}
                  {(['docx', 'pdf', 'both'] as const)
                    .filter((f) => !isWeb || f === 'docx')
                    .map((f) => (
                      <button
                        key={f}
                        onClick={() => setExportFormat(f)}
                        style={{
                          flex: 1,
                          padding: '8px 12px',
                          borderRadius: 6,
                          border:
                            exportFormat === f ? '1px solid var(--ac)' : '1px solid var(--bd)',
                          background: exportFormat === f ? 'rgba(var(--acr),0.08)' : 'transparent',
                          color: exportFormat === f ? 'var(--ac)' : 'var(--tm)',
                          fontSize: 13,
                          fontWeight: exportFormat === f ? 600 : 500,
                          cursor: 'pointer',
                          textTransform: 'uppercase',
                          letterSpacing: '.04em',
                        }}
                      >
                        {f === 'both' ? 'Both' : f}
                      </button>
                    ))}
                </div>
              </div>

              <div style={{ marginBottom: 14 }}>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: 'var(--tm)',
                    marginBottom: 5,
                    textTransform: 'uppercase',
                    letterSpacing: '.04em',
                  }}
                >
                  Revision Marks
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {(
                    [
                      { v: false, label: 'Clean' },
                      { v: true, label: 'Mark changes' },
                    ] as const
                  ).map((opt) => (
                    <button
                      key={String(opt.v)}
                      onClick={() => setExportRevisionMarks(opt.v)}
                      style={{
                        flex: 1,
                        padding: '8px 12px',
                        borderRadius: 6,
                        border:
                          exportRevisionMarks === opt.v
                            ? '1px solid var(--ac)'
                            : '1px solid var(--bd)',
                        background:
                          exportRevisionMarks === opt.v ? 'rgba(var(--acr),0.08)' : 'transparent',
                        color: exportRevisionMarks === opt.v ? 'var(--ac)' : 'var(--tm)',
                        fontSize: 13,
                        fontWeight: exportRevisionMarks === opt.v ? 600 : 500,
                        cursor: 'pointer',
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                <div style={{ fontSize: 10, color: 'var(--tm)', marginTop: 4 }}>
                  Mark changes = strike/underline/highlight every edit since each spec&rsquo;s last
                  issuance, in that spec&rsquo;s revision style. Specs never issued export clean.
                </div>
              </div>

              {isWeb ? (
                /* Web: no local filesystem — files arrive as browser downloads. */
                <div style={{ fontSize: 11, color: 'var(--tm)', marginBottom: 20 }}>
                  Files download to your browser&rsquo;s download folder.
                </div>
              ) : (
                <div style={{ marginBottom: 20 }}>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: 'var(--tm)',
                      marginBottom: 5,
                      textTransform: 'uppercase',
                      letterSpacing: '.04em',
                    }}
                  >
                    Output Folder
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <input
                      value={exportDir}
                      readOnly
                      placeholder="Choose a folder…"
                      style={{
                        flex: 1,
                        padding: '8px 12px',
                        borderRadius: 6,
                        border: '1px solid var(--bd)',
                        background: 'var(--ib)',
                        color: 'var(--tx)',
                        fontSize: 13,
                      }}
                    />
                    <button
                      onClick={async () => {
                        const dir = await (window as any).api?.selectFolderDialog?.();
                        if (dir) setExportDir(dir);
                      }}
                      style={{
                        padding: '8px 14px',
                        borderRadius: 6,
                        border: '1px solid var(--bd)',
                        background: 'var(--sf)',
                        color: 'var(--tx)',
                        fontSize: 13,
                        fontWeight: 500,
                        cursor: 'pointer',
                      }}
                    >
                      Browse
                    </button>
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <Button
                  variant="outline"
                  disabled={exporting}
                  onClick={() => setShowExportModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  disabled={exporting}
                  onClick={async () => {
                    const sortedSpecs = projectSpecs
                      .slice()
                      .sort((a, b) => (a.csiNumber || '').localeCompare(b.csiNumber || ''));
                    const targetSpecs = sortedSpecs.filter((s) => exportSelIds.has(s.id));
                    if (!targetSpecs.length) {
                      toast('Select at least one spec to export', true);
                      return;
                    }

                    // ── Export gate: refuse BEFORE asking for a folder ──
                    // A header must never ship a literal "{P4}". Checking here
                    // (rather than after the folder picker) means the user is
                    // never made to choose a destination for an export that
                    // was always going to be refused.
                    const projectFormaCopyPre =
                      formas.find(
                        (f: any) => (f as any).projectId === editing?.id && !(f as any).specId
                      ) || null;
                    const dialogForma = exportFormaId
                      ? formas.find((f) => f.id === exportFormaId) || null
                      : null;
                    const unboundAll = new Set<string>();
                    let blockedCount = 0;
                    for (const src of targetSpecs as any[]) {
                      let bound: any = src.formaId
                        ? formas.find((f) => f.id === src.formaId)
                        : null;
                      if (
                        bound &&
                        !bound.projectId &&
                        projectFormaCopyPre &&
                        (projectFormaCopyPre as any).sourceFormaId === bound.id
                      )
                        bound = projectFormaCopyPre;
                      const eff: any =
                        formas.find((f: any) => (f as any).specId === src.id) ||
                        bound ||
                        projectFormaCopyPre ||
                        dialogForma;
                      if (!eff) continue;
                      const lastIss: any = realIssuances(src.issuances).slice(-1)[0] || null;
                      const missing = unboundPlaceholders(eff, {
                        project: {
                          'Project.Name': editing?.name || '',
                          'Project.ClientProjectName':
                            (editing as any)?.clientProjectName || editing?.name || '',
                          'Project.Number': (editing as any)?.projectNumber || '',
                          'Project.Address': (editing as any)?.address || '',
                          'Client.Name':
                            orgs.find((o) => o.id === (editing as any)?.clientId)?.name || '',
                          ...(exportDeliverable ? { Deliverable: exportDeliverable } : {}),
                          'Issuance.Name': lastIss?.name || '',
                        },
                        issuances: (src.issuances || []) as any[],
                        dateFormat: eff?.docSettings?.dateFormat,
                      });
                      if (missing.length) {
                        blockedCount++;
                        missing.forEach((id) => unboundAll.add(id));
                      }
                    }
                    if (unboundAll.size) {
                      const names = [...unboundAll].map((id) =>
                        id === 'IssuanceDate' ? 'Issuance Date' : id
                      );
                      toast(
                        `Set ${names.join(', ')} in the forma's Project Tokens before exporting — ` +
                          `${blockedCount} spec${blockedCount > 1 ? 's have' : ' has'} a placeholder with no value.`,
                        true
                      );
                      return;
                    }

                    // Auto-prompt folder picker if user clicked Export without choosing a folder
                    // (desktop only — web has no output folder; files download instead)
                    let dir = exportDir;
                    if (!dir && !isWeb) {
                      dir = await (window as any).api?.selectFolderDialog?.();
                      if (!dir) {
                        toast('Pick an output folder first', true);
                        return;
                      }
                      setExportDir(dir);
                    }

                    if (!isWeb && !(window as any).api?.exportProjectSpecs) {
                      toast(
                        'Export API not available — the desktop app needs to restart to pick up the new build.',
                        true
                      );
                      return;
                    }

                    setExporting(true);
                    try {
                      const formaConfig = exportFormaId
                        ? formas.find((f) => f.id === exportFormaId) || null
                        : null;
                      // Resolve any smart-token blocks against the project's
                      // live context (other specs, project metadata, client)
                      // BEFORE handing the payload to main for DOCX generation.
                      const clientForExport = (editing as any)?.clientId
                        ? orgs.find((o) => o.id === (editing as any).clientId)
                        : undefined;
                      const specsPayload = await prepareSpecsForExport(
                        targetSpecs,
                        {
                          project: {
                            id: editing.id || '',
                            name: editing.name,
                            number: (editing as any).projectNumber,
                            address: (editing as any).address,
                            phase: (editing as any).currentPhaseId,
                            clientId: (editing as any).clientId,
                          },
                          allProjectSpecs: projectSpecs,
                          team: (members || []).map((tm: any) => ({
                            id: tm.id,
                            name: tm.name || tm.fullName || tm.displayName,
                            email: tm.email,
                            role: tm.role,
                            title: tm.title,
                          })),
                          client: clientForExport,
                        },
                        { markRevisions: exportRevisionMarks }
                      );
                      console.log('[export-specs] starting', {
                        count: specsPayload.length,
                        format: exportFormat,
                        formaId: exportFormaId,
                        dir,
                      });
                      // Project-linked forma tokens ({Project.Name}, {Client.Name}…)
                      // resolve from the same project data the smart-block engine uses.
                      const projectMeta: Record<string, string> = {
                        'Project.Name': editing.name || '',
                        'Project.ClientProjectName':
                          (editing as any).clientProjectName || editing.name || '',
                        'Project.Number': (editing as any).projectNumber || '',
                        'Project.Address': (editing as any).address || '',
                        'Client.Name': clientForExport?.name || '',
                      };
                      if (exportDeliverable) {
                        projectMeta['Deliverable'] = exportDeliverable;
                        projectMeta['Deliverable.Name'] = exportDeliverable;
                      }
                      // User-defined Project Tokens: resolve each token's
                      // content (its {inserts} against this same meta) and
                      // hand the result to the engine as ProjectToken:<id>.
                      for (const pt of ((editing as any).formaTokens || []) as any[]) {
                        projectMeta['ProjectToken:' + pt.id] = resolveProjectTokenContent(
                          pt.content,
                          projectMeta
                        );
                      }
                      // Per-spec formatting (specs-style inheritance): a spec's
                      // own forma copy → its explicit binding → the project's
                      // forma copy → the forma picked in this dialog. What you
                      // saw in the editor is what exports.
                      const projectFormaCopy =
                        formas.find(
                          (f: any) => (f as any).projectId === editing.id && !(f as any).specId
                        ) || null;
                      const specsWithFormas = specsPayload.map((p, i) => {
                        const src: any = targetSpecs[i];
                        let bound: any = src.formaId
                          ? formas.find((f) => f.id === src.formaId)
                          : null;
                        // The project copy supersedes the master it was forked
                        // from (mirrors the editor's resolution).
                        if (
                          bound &&
                          !(bound as any).projectId &&
                          projectFormaCopy &&
                          (projectFormaCopy as any).sourceFormaId === bound.id
                        ) {
                          bound = projectFormaCopy;
                        }
                        const eff =
                          formas.find((f: any) => (f as any).specId === src.id) ||
                          bound ||
                          projectFormaCopy;
                        // Issuance is a PER-SPEC fact (each spec carries its own
                        // issuance history) — resolve {Issuance.*} and re-resolve
                        // every Project Token against this spec's latest issuance
                        // so "RWB {Project.Number} – {Issuance.Name}" exports
                        // right per spec instead of blank.
                        const lastIss: any = realIssuances(src.issuances).slice(-1)[0] || null;
                        const issMeta: Record<string, string> = {
                          'Issuance.Name': lastIss?.name || '',
                          'Issuance.Date': lastIss?.date || lastIss?.issuedDate || '',
                        };
                        const perSpecMeta: Record<string, string> = { ...issMeta };
                        for (const pt of ((editing as any).formaTokens || []) as any[]) {
                          perSpecMeta['ProjectToken:' + pt.id] = resolveProjectTokenContent(
                            pt.content,
                            { ...projectMeta, ...issMeta }
                          );
                        }
                        // Project placeholders ({P1}…, {IssuanceDate}): the
                        // blanks the master left. Resolved per SPEC because
                        // issuances are per-spec, then handed to the engine
                        // under their own ids — the same substitution path
                        // project tokens already use.
                        const phCtx = {
                          project: { ...projectMeta, ...issMeta },
                          issuances: (src.issuances || []) as any[],
                          dateFormat: (eff as any)?.docSettings?.dateFormat,
                        };
                        const phValues = eff ? resolvePlaceholders(eff as any, phCtx) : {};
                        const missing = eff ? unboundPlaceholders(eff as any, phCtx) : [];
                        const overrides = {
                          ...perSpecMeta,
                          ...phValues,
                          ...(src.tokenOverrides && Object.keys(src.tokenOverrides).length
                            ? src.tokenOverrides
                            : {}),
                        };
                        return {
                          ...p,
                          formaConfig: eff ? JSON.parse(JSON.stringify(eff)) : undefined,
                          metaOverrides: overrides,
                          unboundPlaceholders: missing,
                          specLabel: src.csiNumber || src.title || src.id,
                        };
                      });

                      // ── Export gate (non-negotiable): a header must never
                      // ship a literal "{P4}". Naming the specs AND the blanks
                      // is the whole message — the fix is in Project Tokens.
                      const blocked = specsWithFormas.filter(
                        (s: any) => s.unboundPlaceholders?.length
                      );
                      if (blocked.length) {
                        const names = [
                          ...new Set(blocked.flatMap((s: any) => s.unboundPlaceholders)),
                        ].map((id) => (id === 'IssuanceDate' ? 'Issuance Date' : id));
                        toast(
                          `Set ${names.join(', ')} in the forma's Project Tokens before exporting — ` +
                            `${blocked.length} spec${blocked.length > 1 ? 's have' : ' has'} a placeholder with no value.`,
                          true
                        );
                        setExporting(false);
                        return;
                      }
                      specsWithFormas.forEach((s: any) => {
                        delete s.unboundPlaceholders;
                        delete s.specLabel;
                      });
                      // Same payload both targets: desktop hands it to the local
                      // Word engine via IPC; web POSTs it to the server engine
                      // and downloads the resulting .docx files.
                      const res = isWeb
                        ? await exportSpecsViaServer({
                            specs: specsWithFormas,
                            formaConfig: formaConfig
                              ? JSON.parse(JSON.stringify(formaConfig))
                              : null,
                            projectMeta,
                          })
                        : await (window as any).api.exportProjectSpecs({
                            specs: specsWithFormas,
                            formaConfig: formaConfig
                              ? JSON.parse(JSON.stringify(formaConfig))
                              : null,
                            outputDir: dir,
                            format: exportFormat,
                            projectMeta,
                          });
                      console.log('[export-specs] result', res);
                      const docxOk = (res?.docx || []).filter((r: any) => r.success).length;
                      const pdfOk = (res?.pdf || []).filter((r: any) => r.success).length;
                      const fails = [
                        ...(res?.docx || []).filter((r: any) => !r.success),
                        ...(res?.pdf || []).filter((r: any) => !r.success),
                      ];
                      if (fails.length) {
                        toast(
                          `Exported with ${fails.length} failure${fails.length === 1 ? '' : 's'}. See console.`,
                          true
                        );
                        console.warn('[export] failures:', fails);
                      } else {
                        const parts: string[] = [];
                        if (exportFormat !== 'pdf') parts.push(`${docxOk} docx`);
                        if (exportFormat !== 'docx') parts.push(`${pdfOk} pdf`);
                        toast(
                          isWeb
                            ? `Downloaded ${parts.join(' + ')}`
                            : `Exported ${parts.join(' + ')} to ${exportDir}`
                        );
                      }
                      if (!isWeb) (window as any).api?.openFolder?.(exportDir);
                      setShowExportModal(false);
                    } catch (err: any) {
                      toast(`Export failed: ${err.message || err}`, true);
                    } finally {
                      setExporting(false);
                    }
                  }}
                >
                  {exporting ? 'Exporting…' : 'Export'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ═══════════ PROJECT LIST VIEW ═══════════
  return (
    <div
      style={{
        padding: 24,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
          flexShrink: 0,
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Project Hub</h2>
          <p style={{ fontSize: 13, color: 'var(--tm)', margin: '4px 0 0' }}>
            {projects.length} project{projects.length !== 1 ? 's' : ''} &middot; Double-click cells
            to edit inline &middot; Ctrl+V to paste
          </p>
        </div>
        {/* Project Drive mapping — DESKTOP ONLY. Browsers can't reach local/network
            drives; this returns later as a cloud-storage API integration (e.g. Egnyte). */}
        {!isWeb && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '6px 10px',
              borderRadius: 6,
              border: '1px solid var(--bd)',
              background: 'var(--sf)',
              flexWrap: 'wrap',
            }}
          >
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: 'var(--tm)',
                textTransform: 'uppercase',
                letterSpacing: '.04em',
              }}
            >
              Project Drive
            </span>
            <span
              title={projectDriveRoot || 'No drive set'}
              style={{
                fontSize: 11,
                color: projectDriveRoot ? 'var(--tx)' : 'var(--tm)',
                fontFamily: "'JetBrains Mono', monospace",
                maxWidth: 260,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {projectDriveRoot || '— not set —'}
            </span>
            <button
              onClick={pickProjectDriveRoot}
              style={{
                padding: '4px 10px',
                borderRadius: 4,
                border: '1px solid var(--bd)',
                background: 'transparent',
                color: 'var(--tm)',
                fontSize: 11,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {projectDriveRoot ? 'Change' : 'Pick Folder'}
            </button>
            {projectDriveRoot && (
              <button
                onClick={() => {
                  if (
                    !confirm(
                      'Disconnect the Project Drive? This clears the root path on this laptop but leaves existing project-folder links untouched.'
                    )
                  )
                    return;
                  saveProjectDriveRoot('');
                  toast('Project Drive disconnected');
                }}
                title="Stop using a project drive root on this laptop"
                style={{
                  padding: '4px 10px',
                  borderRadius: 4,
                  border: '1px solid var(--bd)',
                  background: 'transparent',
                  color: 'var(--dg)',
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Disconnect
              </button>
            )}
            <button
              onClick={autoMatchProjectDrive}
              disabled={!projectDriveRoot || scanningDrive}
              title="Scan the drive root and link each project to its matching folder by project number"
              style={{
                padding: '4px 10px',
                borderRadius: 4,
                border: 'none',
                background: !projectDriveRoot || scanningDrive ? 'var(--sa)' : 'var(--ac)',
                color:
                  !projectDriveRoot || scanningDrive ? 'var(--tm)' : 'var(--color-text-inverse)',
                fontSize: 11,
                fontWeight: 600,
                cursor: !projectDriveRoot || scanningDrive ? 'not-allowed' : 'pointer',
              }}
            >
              {scanningDrive ? 'Scanning…' : 'Auto-match'}
            </button>
          </div>
        )}
      </div>

      {/* Summary Cards */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: 12,
          marginBottom: 24,
          flexShrink: 0,
        }}
      >
        {[
          {
            label: 'ACTIVE PROJECTS',
            icon: 'M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z',
            body: (
              <div
                style={{
                  fontSize: 28,
                  fontWeight: 800,
                  fontFamily: "'JetBrains Mono', monospace",
                  color: 'var(--ac)',
                  lineHeight: 1,
                }}
              >
                {activeProjects.length}
              </div>
            ),
          },
          {
            label: 'CLOSED',
            icon: 'M22 11.08V12a10 10 0 1 1-5.93-9.14 M22 4 12 14.01l-3-3',
            body: (
              <div
                style={{
                  fontSize: 28,
                  fontWeight: 800,
                  fontFamily: "'JetBrains Mono', monospace",
                  lineHeight: 1,
                }}
              >
                {closedProjects.length}
              </div>
            ),
          },
          {
            label: 'PHASES',
            icon: 'M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z M4 22v-7',
            body: <ProgressBar {...phaseStats} />,
          },
          {
            label: 'DELIVERABLES',
            icon: 'm7.5 4.27 9 5.15 M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z M3.3 7 12 12l8.7-5 M12 22V12',
            body: <ProgressBar {...deliverableStats} />,
          },
        ].map((c) => (
          <div key={c.label} style={cardStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 11 }}>
              <div
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 9,
                  flexShrink: 0,
                  background: 'rgba(var(--acr),0.12)',
                  color: 'var(--ac)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.9"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d={c.icon} />
                </svg>
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: 'var(--tm)',
                  fontWeight: 700,
                  letterSpacing: '.04em',
                }}
              >
                {c.label}
              </div>
            </div>
            {c.body}
          </div>
        ))}
      </div>

      <DataTable<Project>
        columns={columns}
        data={projects}
        keyField="id"
        storageKey="projects"
        onRowClick={(p) => {
          if (deleteMode) {
            toggleDeleteSelect(p.id);
            return;
          }
          openEdit(p);
        }}
        groupOptions={groupOptions}
        initialSort={{ key: 'projectNumber', direction: 'asc' }}
        emptyIcon="[+]"
        emptyTitle="No Projects Yet"
        emptyMessage="Create a project to get started."
        toolbarExtra={
          <div style={{ display: 'flex', gap: 6 }}>
            {deleteMode ? (
              <>
                <Button
                  variant="destructive"
                  onClick={bulkDelete}
                  disabled={deleteSelected.size === 0}
                >
                  Delete ({deleteSelected.size})
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setDeleteMode(false);
                    setDeleteSelected(new Set());
                  }}
                >
                  Cancel
                </Button>
              </>
            ) : (
              <>
                {canDeleteProject && (
                  <Button
                    variant="outline"
                    onClick={() => setDeleteMode(true)}
                    className="text-danger border-danger hover:opacity-80"
                  >
                    Delete
                  </Button>
                )}
                {canCreateProject && <Button onClick={openNew}>+ New Project</Button>}
              </>
            )}
          </div>
        }
      />

      {/* Old dialog removed — replaced by full-page detail view above */}

      {/* Import unmatched drive folders as new projects */}
      {showImportModal && unmatchedFolders.length > 0 && (
        <div
          onMouseDown={(e) => {
            if (e.target === e.currentTarget && !importingFolders) setShowImportModal(false);
          }}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: 'var(--sf)',
              borderRadius: 10,
              border: '1px solid var(--bd)',
              padding: 24,
              width: 640,
              maxHeight: '85vh',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 8px 32px rgba(0,0,0,.2)',
            }}
          >
            <div style={{ flexShrink: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>
                Import folders as projects
              </div>
              <div style={{ fontSize: 13, color: 'var(--tm)', marginBottom: 16 }}>
                Found {unmatchedFolders.length} folder{unmatchedFolders.length === 1 ? '' : 's'} on
                the drive whose project number doesn't exist in the app yet. Pick which ones to
                create.
              </div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                <button
                  onClick={() =>
                    setUnmatchedFolders(unmatchedFolders.map((u) => ({ ...u, include: true })))
                  }
                  style={{
                    padding: '4px 10px',
                    borderRadius: 4,
                    border: '1px solid var(--bd)',
                    background: 'transparent',
                    color: 'var(--tm)',
                    fontSize: 11,
                    cursor: 'pointer',
                  }}
                >
                  Select all
                </button>
                <button
                  onClick={() =>
                    setUnmatchedFolders(unmatchedFolders.map((u) => ({ ...u, include: false })))
                  }
                  style={{
                    padding: '4px 10px',
                    borderRadius: 4,
                    border: '1px solid var(--bd)',
                    background: 'transparent',
                    color: 'var(--tm)',
                    fontSize: 11,
                    cursor: 'pointer',
                  }}
                >
                  Clear all
                </button>
                <span
                  style={{
                    marginLeft: 'auto',
                    fontSize: 11,
                    color: 'var(--tm)',
                    alignSelf: 'center',
                  }}
                >
                  {unmatchedFolders.filter((u) => u.include).length} selected
                </span>
              </div>
            </div>
            <div
              style={{
                flex: 1,
                overflow: 'auto',
                border: '1px solid var(--bd)',
                borderRadius: 6,
                background: 'var(--ib)',
              }}
            >
              {unmatchedFolders.map((u, i) => (
                <label
                  key={u.folderPath}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '8px 12px',
                    borderBottom: i < unmatchedFolders.length - 1 ? '1px solid var(--bd)' : 'none',
                    cursor: 'pointer',
                    background: u.include ? 'rgba(var(--acr),0.04)' : 'transparent',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={u.include}
                    onChange={(e) =>
                      setUnmatchedFolders((prev) =>
                        prev.map((x, idx) => (idx === i ? { ...x, include: e.target.checked } : x))
                      )
                    }
                    style={{ flexShrink: 0, cursor: 'pointer' }}
                  />
                  <span
                    style={{
                      fontFamily: "'JetBrains Mono', monospace",
                      fontSize: 11,
                      fontWeight: 700,
                      color: 'var(--ac)',
                      minWidth: 80,
                    }}
                  >
                    {u.projectNumber}
                  </span>
                  <input
                    value={u.editedName}
                    onChange={(e) =>
                      setUnmatchedFolders((prev) =>
                        prev.map((x, idx) => (idx === i ? { ...x, editedName: e.target.value } : x))
                      )
                    }
                    placeholder="Project name"
                    style={{
                      flex: 1,
                      padding: '4px 8px',
                      border: '1px solid var(--bd)',
                      borderRadius: 4,
                      background: 'var(--sf)',
                      color: 'var(--tx)',
                      fontSize: 13,
                    }}
                  />
                  <span
                    title={u.folderPath}
                    style={{
                      fontSize: 10,
                      color: 'var(--tm)',
                      fontFamily: "'JetBrains Mono', monospace",
                      maxWidth: 160,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                    }}
                  >
                    {u.folderPath.split(/[\\/]/).slice(-2).join('/')}
                  </span>
                </label>
              ))}
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 8,
                marginTop: 16,
                flexShrink: 0,
              }}
            >
              <Button
                variant="outline"
                onClick={() => {
                  setShowImportModal(false);
                  setUnmatchedFolders([]);
                }}
                disabled={importingFolders}
              >
                Cancel
              </Button>
              <Button
                onClick={importUnmatchedFolders}
                disabled={
                  importingFolders || unmatchedFolders.filter((u) => u.include).length === 0
                }
              >
                {importingFolders
                  ? 'Importing…'
                  : `Import ${unmatchedFolders.filter((u) => u.include).length}`}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ProgressBar({
  pending,
  inProgress,
  complete,
  total,
}: {
  pending: number;
  inProgress: number;
  complete: number;
  total: number;
}) {
  if (total === 0) return <div style={{ fontSize: 11, color: 'var(--tm)' }}>None yet</div>;
  return (
    <div>
      <div
        style={{
          display: 'flex',
          height: 6,
          borderRadius: 3,
          overflow: 'hidden',
          background: 'var(--sa)',
          marginBottom: 4,
        }}
      >
        <div style={{ width: `${(complete / total) * 100}%`, background: 'var(--ok)' }} />
        <div
          style={{
            width: `${(inProgress / total) * 100}%`,
            background: 'var(--color-warning)',
          }}
        />
      </div>
      <div style={{ display: 'flex', gap: 12, fontSize: 10, color: 'var(--tm)' }}>
        <span>{complete} done</span>
        <span>{inProgress} active</span>
        <span>{pending} pending</span>
      </div>
    </div>
  );
}

const sectionHeadingStyle: React.CSSProperties = {
  fontSize: 14,
  fontWeight: 700,
  marginBottom: 10,
  color: 'var(--tx)',
};
const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '7px 10px',
  borderRadius: 5,
  border: '1px solid var(--bd)',
  background: 'var(--ib)',
  color: 'var(--tx)',
  fontSize: 13,
};
const labelStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  color: 'var(--tm)',
  marginBottom: 4,
};
const btnPrimary: React.CSSProperties = {
  padding: '8px 16px',
  borderRadius: 6,
  border: 'none',
  background: 'var(--ac)',
  color: 'var(--color-text-inverse)',
  fontWeight: 600,
  fontSize: 13,
  cursor: 'pointer',
};
const cardStyle: React.CSSProperties = {
  background: 'var(--sf)',
  borderRadius: 10,
  border: '1px solid var(--bd)',
  padding: '16px 20px',
};
const miniInputStyle: React.CSSProperties = {
  width: '100%',
  padding: '3px 6px',
  borderRadius: 3,
  border: '1px solid var(--bd)',
  background: 'var(--ib)',
  color: 'var(--tx)',
  fontSize: 13,
  boxSizing: 'border-box' as const,
};

function MemberPickerInline({
  availableMembers,
  onSelect,
}: {
  availableMembers: TeamMember[];
  onSelect: (id: string) => void;
}) {
  const [search, setSearch] = React.useState('');
  const filtered = availableMembers.filter((m) =>
    (m.name || '').toLowerCase().includes(search.toLowerCase())
  );
  return (
    <div>
      <input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search members..."
        style={{ ...miniInputStyle, marginBottom: 6 }}
        autoFocus
      />
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          maxHeight: 200,
          overflow: 'auto',
        }}
      >
        {filtered.map((m) => (
          <div
            key={m.id}
            onClick={() => onSelect(m.id)}
            style={{
              padding: '5px 10px',
              borderRadius: 5,
              cursor: 'pointer',
              fontSize: 13,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              background: 'transparent',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--sa)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
          >
            <span
              style={{
                width: 26,
                height: 26,
                borderRadius: '50%',
                background: 'var(--ac)',
                color: 'var(--color-text-inverse)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 10,
                fontWeight: 700,
                flexShrink: 0,
              }}
            >
              {(m.name || '?')[0]}
            </span>
            <span>{m.name || 'Unnamed'}</span>
          </div>
        ))}
        {filtered.length === 0 && (
          <div
            style={{
              fontSize: 11,
              color: 'var(--tm)',
              fontStyle: 'italic',
              padding: 8,
              textAlign: 'center',
            }}
          >
            No available members
          </div>
        )}
      </div>
    </div>
  );
}

(window as any).Projects = Projects;
export { Projects };
